<!--
  SkuSetup -- SuperAdmin per-SKU setup page for NON-INTEGRATED
  (pseudo-) suppliers.

  Design handoff: planning/design-handoffs/handoff_2026-08-26/
    2026-08-26-zone-geometry/ (README, ADR 0071, design/sku-setup.jsx).
  Build assessment: docs/planning/2026-08-26-zone-geometry-editor-build-assessment.md

  WHY THIS PAGE EXISTS
  ====================
  For suppliers Artifi covers, Artifi owns preview position and scale
  and we tell it nothing. For suppliers it does NOT cover, ThreadFlow
  has to render previews itself, which needs two facts nothing in the
  product authors today: where decoration is allowed on a garment
  photo, and how big an inch is in that photo. This page is the
  superadmin surface that produces both, as a per-SKU walk:

      1 Details -> 2 Sizes -> 3 Colors & images -> 4 Geometry

  SCOPE OF THIS FILE (phase 3a + 3b)
  ==================================
  Sections 1-3 and the page shell. Section 4 (Geometry) is a
  PLACEHOLDER on purpose -- see the template comment above it. It is
  not "unfinished"; it is blocked on a schema decision (the
  per-garment printable-area override has no home) plus a migration,
  neither of which is made yet.

  SUPERADMIN ONLY (ADR 0071 / research F2). Distributors and shoppers
  never see this. `placement_zone_boxes` has no `distributor_id`
  column, which is precisely why the surface is SA-gated rather than
  tenant-scoped -- do not add tenancy logic here.

  CHROME: the handoff specifies a 64px navy app bar. This page does
  NOT render one. SuperAdminLayout already supplies the SA topbar and
  sidebar (meta.layout = 'superadmin'), and a second bar would double
  the chrome. The handoff's app-bar spec describes its standalone
  prototype, which has no layout around it. The rail, header, state
  strip and sections below follow the handoff exactly.

  STATE: fixture-backed, deliberately. Phase 3d is "wire to the real
  API; replace fixtures with live data" -- there is no read or write
  endpoint for SKU setup state yet (only `local_garment_images` and
  `placement_zone_boxes` as tables, seeded by PseudoSupplierSeeder).
  Every mutation below is local component state. The seam is a single
  `loadProducts()` call so 3d replaces one method, not the page.

  data-testid prefix: sku-setup-*
-->
<template>
    <section
        class="sku-setup"
        data-testid="sku-setup-root"
    >
        <!-- The rail: the supplier's SKUs, REPORTING gaps, deciding
             nothing (ADR 0071 decision 6). -->
        <aside
            class="sku-setup__rail"
            data-testid="sku-setup-rail"
        >
            <div class="sku-setup__rail-head">
                <span class="sku-setup__eyebrow">Products</span>
                <span class="sku-setup__rail-actions">
                    <!-- The primary action on this rail. Blue is the
                         app's add colour (Vuetify `primary`, main.js:55)
                         and `--tf-primary` is the same #119ae9, so this
                         matches the store editor's Create Product and
                         the SA New Distributor rather than adding a
                         third blue. GH #3085 item 3. -->
                    <button
                        type="button"
                        class="sku-setup__btn sku-setup__btn--xs sku-setup__btn--primary"
                        data-testid="sku-setup-add-product"
                        @click="addProduct"
                    >
                        + Add
                    </button>
                    <!-- The rail-header Delete that used to sit here is
                         GONE (GH #3085 item 2). It acted on whichever
                         SKU was selected, which a header control cannot
                         show you, so the affordance sat nowhere near
                         the thing it destroyed. Its two replacements --
                         per-row below, and in the detail header -- each
                         name their target, and both open the SAME
                         confirm panel: more ways in, still one guard. -->
                </span>
            </div>
            <div class="sku-setup__rail-list">
                <!-- Row and delete are SIBLINGS inside a wrapper, not a
                     button inside a button. Nesting them would be
                     invalid HTML, and the browser's recovery from it
                     puts the delete OUTSIDE the row it belongs to. The
                     wrapper carries the row's hover/selected chrome so
                     the pair still reads as one line. -->
                <div
                    v-for="product in products"
                    :key="product.sku"
                    class="sku-setup__rail-item"
                    :class="{
                        'sku-setup__rail-item--on': product.sku === selectedSku,
                    }"
                >
                    <button
                        type="button"
                        class="sku-setup__rail-row"
                        :data-testid="`sku-setup-rail-row-${product.sku}`"
                        @click="selectSku(product.sku)"
                    >
                        <span class="sku-setup__rail-body">
                            <span class="sku-setup__rail-name">
                                {{ product.name || "Untitled product" }}
                            </span>
                            <span class="sku-setup__rail-meta">
                                {{ product.itemNumber || product.sku }} &middot;
                                {{ product.colors.length }} colors
                            </span>
                            <!-- The gap line. Copy is specified by the
                                 handoff, not paraphrased. -->
                            <span
                                class="sku-setup__rail-gaps"
                                :data-testid="`sku-setup-rail-gaps-${product.sku}`"
                            >{{ railGapLine(product) }}</span>
                        </span>
                        <span
                            v-if="stateFor(product.sku).ready"
                            class="material-icons sku-setup__rail-ready"
                            :data-testid="`sku-setup-rail-ready-${product.sku}`"
                        >check_circle</span>
                    </button>
                    <!-- Deleting from the list must NOT first select the
                         row: selecting is what the row click means, and
                         borrowing it here would move the editor off the
                         product being edited every time someone tidies up a
                         different one. So this passes its own sku.

                         Guarded on length like the old header control was
                         -- the page has no empty state, and the last row is
                         the one whose removal would leave a dangling
                         selection. -->
                    <button
                        v-if="products.length > 1"
                        type="button"
                        class="sku-setup__rail-del"
                        :data-testid="`sku-setup-rail-delete-${product.sku}`"
                        :aria-label="`Delete ${product.name || product.sku}`"
                        :title="`Delete ${product.name || product.sku}`"
                        @click="startDeleteSku(product.sku)"
                    >
                        <span class="material-icons">delete_outline</span>
                    </button>
                </div>
            </div>
        </aside>

        <div class="sku-setup__main">
            <div class="sku-setup__col">
                <!--
                  NOTHING SELECTED MEANS NO FORM.

                  Jake, testing 8083: "the page started with no products
                  but the form was visible, and broken since there was no
                  product."

                  `product` returns a shaped EMPTY placeholder when there
                  is nothing to show, which is what keeps the ~40 computeds
                  below it total instead of scattering `?.` through this
                  template. That part is deliberate and stays. What was
                  missing is the gate the placeholder was designed for:
                  `hasProduct` existed and its own comment claimed "the
                  editor is gated on `hasProduct` anyway", but the computed
                  appeared NOWHERE in this template. So the four sections
                  rendered against the empty object -- every field blank,
                  every control live, and every edit dropped on the floor
                  by `setProduct`, which returns early when no product
                  matches. A form that accepts input and stores none is
                  worse than no form.

                  TWO STATES, TWO MESSAGES. They call for different
                  actions: an empty supplier needs a product CREATED, a
                  full rail with nothing selected needs one PICKED. One
                  shared message would name the wrong next action for
                  whichever case it did not mean.
                -->

                <!--
                  THE WAY BACK OUT, ABOVE THE GATE.

                  The eyebrow is page CHROME, not form chrome: it answers
                  "where am I, and how do I leave", which the page must be
                  able to answer in every state it can land in. It used to
                  live inside the `v-else` below, so it disappeared in
                  exactly the two states where it matters most -- an empty
                  supplier, and a rail with nothing picked -- leaving
                  browser Back as the only exit.

                  Not hypothetical: the seeded pseudo-supplier ([TEST]
                  Cutter & Buck) sorts FIRST by name in the non-integrated
                  list and owns zero `supplier_skus` rows, so the first
                  "SKU setup" link a human clicks lands here.
                -->
                <div class="sku-setup__eyebrow">
                    <router-link
                        class="sku-setup__back"
                        to="/admin/suppliers/non-integrated"
                        data-testid="sku-setup-back-to-suppliers"
                    >Non-integrated suppliers</router-link>
                    &middot;
                    <span data-testid="sku-setup-supplier-name">{{
                        supplierLine
                    }}</span>
                </div>

                <div
                    v-if="!products.length"
                    class="sku-setup__blank"
                    data-testid="sku-setup-no-products"
                >
                    <h2 class="sku-setup__blank-title">
                        No products yet
                    </h2>
                    <p class="sku-setup__blank-body">
                        This supplier has no products set up. Use
                        <strong>+ Add</strong> in the Products list to
                        create the first one.
                    </p>
                </div>
                <div
                    v-else-if="!hasSelection"
                    class="sku-setup__blank"
                    data-testid="sku-setup-no-selection"
                >
                    <h2 class="sku-setup__blank-title">
                        No product selected
                    </h2>
                    <p class="sku-setup__blank-body">
                        Select a product from the Products list to set up
                        its details, sizes, colors and geometry.
                    </p>
                </div>

                <template v-else>
                <!-- Page header + state strip -->
                <header
                    class="sku-setup__header"
                    data-testid="sku-setup-header"
                >
                    <div class="sku-setup__header-left">
                        <!--
                          The supplier this page is SCOPED TO is named in
                          the eyebrow ABOVE this header, outside the gate
                          that hides the form -- see the comment there.
                          Through phase 3a/3b it was a hardcoded fixture
                          string, because the route
                          (`/admin/suppliers/local/sku-setup`) named a
                          CLASS and the page had no supplier identity to
                          show. It has one now.
                        -->
                        <h2
                            class="sku-setup__title"
                            data-testid="sku-setup-product-name"
                        >
                            {{ product.name || "Untitled product" }}
                        </h2>
                        <div
                            class="sku-setup__strip"
                            data-testid="sku-setup-state-strip"
                        >
                            <span
                                v-for="item in stateStrip"
                                :key="item.label"
                                class="sku-setup__strip-item"
                                :data-testid="`sku-setup-strip-${item.key}`"
                            >
                                <span
                                    class="sku-setup__dot"
                                    :class="{
                                        'sku-setup__dot--done': item.done,
                                    }"
                                />
                                <b>{{ item.label }}</b> {{ item.detail }}
                            </span>
                        </div>
                    </div>
                    <div class="sku-setup__header-right">
                        <div
                            class="sku-setup__gaps"
                            data-testid="sku-setup-gaps"
                        >{{ gaps.length ? gaps.join(" &middot; ") : "Nothing outstanding" }}</div>
                        <!-- ADR 0071 decision 6, verbatim. Marking
                             ready is never computed and never blocked;
                             the gaps are stated beside the button. -->
                        <div class="sku-setup__gaps-note">
                            Marking ready is your call, not the tool's.
                        </div>
                    </div>
                    <!-- The auto-save indicator.
                         Rendered only when there is something to say, so
                         an untouched page carries no chrome. On failure
                         it names the SERVER's reason and NEVER the word
                         "saved": a silent auto-save failure is worse than
                         no auto-save, because the user believes their
                         work is stored and navigates away from it. -->
                    <span
                        v-if="saveStateLabel"
                        class="sku-setup__save-state"
                        :class="{
                            'sku-setup__save-state--error':
                                saveState === 'error',
                        }"
                        role="status"
                        aria-live="polite"
                        data-testid="sku-setup-save-state"
                    >{{ saveStateLabel }}</span>
                    <button
                        type="button"
                        class="sku-setup__btn"
                        :class="{
                            'sku-setup__btn--primary': !state.ready,
                        }"
                        data-testid="sku-setup-mark-ready"
                        @click="toggleReady"
                    >
                        {{ markReadyLabel }}
                    </button>
                    <!-- Delete the product you are looking at, from the
                         page that shows you what it holds (GH #3085
                         item 2). Same confirm panel as the rail's
                         per-row control -- more entry points, one
                         guard. Guarded on length for the same reason:
                         removing the only product would leave the
                         editor pointing at nothing. -->
                    <button
                        v-if="products.length > 1"
                        type="button"
                        class="sku-setup__btn sku-setup__btn--danger"
                        data-testid="sku-setup-delete-current"
                        @click="startDeleteSku(selectedSku)"
                    >
                        Delete product
                    </button>
                </header>

                <!-- 1 - Details -->
                <section
                    class="sku-setup__card"
                    data-testid="sku-setup-section-details"
                >
                    <div class="sku-setup__card-head">
                        <span class="sku-setup__secnum">1</span>
                        <h3 class="sku-setup__card-title">
                            Details
                        </h3>
                    </div>
                    <div class="sku-setup__details">
                        <label class="sku-setup__field">
                            <span class="sku-setup__label">Item number *</span>
                            <input
                                class="sku-setup__input sku-setup__input--mono"
                                :value="product.itemNumber"
                                placeholder="CB0000"
                                data-testid="sku-setup-item-number"
                                @input="setProduct({ itemNumber: $event.target.value })"
                            >
                        </label>
                        <label class="sku-setup__field">
                            <span class="sku-setup__label">Product name *</span>
                            <input
                                class="sku-setup__input"
                                :value="product.name"
                                placeholder="e.g. Coastal Jogger"
                                data-testid="sku-setup-product-name-input"
                                @input="setProduct({ name: $event.target.value })"
                            >
                        </label>
                        <label class="sku-setup__field">
                            <span class="sku-setup__label">
                                Garment shape &mdash; decides which zones exist
                            </span>
                            <!-- Load-bearing, not cosmetic: the shape
                                 decides which zones exist and where they
                                 sit, so changing it CLEARS geometry
                                 rather than silently reinterpreting
                                 boxes against a different garment
                                 (ADR 0071 decision 7). -->
                            <select
                                class="sku-setup__input"
                                :value="product.cls"
                                data-testid="sku-setup-garment-shape"
                                @change="setGarmentShape($event.target.value)"
                            >
                                <option
                                    v-for="shape in GARMENT_SHAPES"
                                    :key="shape"
                                    :value="shape"
                                >
                                    {{ shape }}
                                </option>
                            </select>
                        </label>
                        <label class="sku-setup__field">
                            <span class="sku-setup__label">Decoration methods</span>
                            <input
                                class="sku-setup__input"
                                value="Embroidery, Screen print"
                                readonly
                                data-testid="sku-setup-decoration-methods"
                            >
                        </label>
                    </div>
                </section>

                <!-- 2 - Sizes -->
                <section
                    class="sku-setup__card"
                    data-testid="sku-setup-section-sizes"
                >
                    <div class="sku-setup__card-head">
                        <span class="sku-setup__secnum">2</span>
                        <h3 class="sku-setup__card-title">
                            Sizes
                        </h3>
                    </div>
                    <div class="sku-setup__sizes">
                        <div class="sku-setup__size-tags">
                            <button
                                v-for="size in offeredSizeVocabulary"
                                :key="size"
                                type="button"
                                class="sku-setup__tag"
                                :class="{
                                    'sku-setup__tag--on': state.sizes.includes(size),
                                }"
                                :data-testid="`sku-setup-size-${size}`"
                                :aria-pressed="state.sizes.includes(size) ? 'true' : 'false'"
                                @click="toggleSize(size)"
                            >
                                {{ size }}
                            </button>
                        </div>
                        <div class="sku-setup__sizeclass">
                            <span class="sku-setup__label">Size class</span>
                            <div class="sku-setup__seg">
                                <button
                                    v-for="cls in SIZE_CLASSES"
                                    :key="cls"
                                    type="button"
                                    :class="{
                                        'sku-setup__seg--on': state.sizeClass === cls,
                                    }"
                                    :data-testid="`sku-setup-sizeclass-${cls}`"
                                    @click="setState({ sizeClass: cls })"
                                >
                                    {{ cls }}
                                </button>
                            </div>
                            <!-- ADR 0065's size_scale_factor. Stated as
                                 a consequence in words, per the handoff:
                                 it shrinks every printable area, so it
                                 participates in fit maths downstream. -->
                            <span
                                class="sku-setup__label"
                                data-testid="sku-setup-scale-factor"
                            >scale factor {{ sizeScaleFactor }} &mdash; shrinks every printable area</span>
                        </div>
                    </div>
                </section>

                <!-- 3 - Colors & images -->
                <section
                    class="sku-setup__card"
                    data-testid="sku-setup-section-colors"
                >
                    <div class="sku-setup__card-head">
                        <span class="sku-setup__secnum">3</span>
                        <h3 class="sku-setup__card-title">
                            Colors &amp; images
                        </h3>
                        <span class="sku-setup__hint">
                            One blank photo per color per view.
                        </span>
                    </div>

                    <div
                        v-if="!product.colors.length"
                        data-testid="sku-setup-colors-empty"
                    >
                        <div class="sku-setup__hint">
                            No colors yet. A color is a name, a swatch, and
                            one blank photo per view.
                        </div>
                        <button
                            type="button"
                            class="sku-setup__btn sku-setup__btn--primary sku-setup__btn--xs"
                            data-testid="sku-setup-add-color"
                            @click="addColor"
                        >
                            + Add color
                        </button>
                    </div>

                    <table
                        v-else
                        class="sku-setup__tbl"
                        data-testid="sku-setup-colors-table"
                    >
                        <thead>
                            <tr>
                                <th>Color</th>
                                <th
                                    v-for="view in views"
                                    :key="view.code"
                                >
                                    {{ view.label }} image
                                </th>
                                <th>Geometry</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="color in product.colors"
                                :key="color.id"
                                :data-testid="`sku-setup-color-row-${color.id}`"
                            >
                                <td>
                                    <span class="sku-setup__color-cell">
                                        <span
                                            class="sku-setup__swatch"
                                            :style="{ background: color.hex }"
                                            :data-testid="`sku-setup-swatch-${color.id}`"
                                        />
                                        <span class="sku-setup__palette">
                                            <button
                                                v-for="hex in PALETTE"
                                                :key="hex"
                                                type="button"
                                                class="sku-setup__palette-chip"
                                                :style="{ background: hex }"
                                                :title="hex"
                                                :data-testid="`sku-setup-recolor-${color.id}-${hex.slice(1)}`"
                                                @click="setColor(color.id, { hex })"
                                            />
                                        </span>
                                        <span class="sku-setup__name-wrap">
                                            <input
                                                class="sku-setup__input sku-setup__input--name"
                                                :value="colorNameFieldValue(color)"
                                                placeholder="Color name"
                                                :data-testid="`sku-setup-color-name-${color.id}`"
                                                @input="draftColorName(color.id, $event.target.value)"
                                                @blur="commitColorName(color.id)"
                                                @keyup.enter="commitColorName(color.id)"
                                            >
                                            <!--
                                              Type-down. Only rendered
                                              while this row is mid-edit
                                              (a draft exists), so a
                                              settled table shows no
                                              dropdowns. `mousedown`
                                              fires BEFORE the input's
                                              blur, which is what lets a
                                              click land on an option
                                              that blur would otherwise
                                              unmount first.
                                            -->
                                            <span
                                                v-if="colorDrafts[color.id] !== undefined"
                                                class="sku-setup__suggestions"
                                                :data-testid="`sku-setup-color-suggestions-${color.id}`"
                                            >
                                                <!--
                                                  A name that matches
                                                  nothing is VALID, not
                                                  rejected. Confirm it
                                                  explicitly: an empty
                                                  panel would read as
                                                  "no", which is the
                                                  impression this whole
                                                  change exists to undo.
                                                -->
                                                <span
                                                    v-if="!colorSuggestionsFor(color.id).length"
                                                    class="sku-setup__freeform"
                                                    :data-testid="`sku-setup-color-freeform-${color.id}`"
                                                >Not a listed color -- will be saved as typed.</span>
                                                <button
                                                    v-for="s in colorSuggestionsFor(color.id)"
                                                    :key="s.code"
                                                    type="button"
                                                    class="sku-setup__suggestion"
                                                    :data-testid="`sku-setup-color-suggestion-${color.id}-${s.code}`"
                                                    @mousedown.prevent="pickColorSuggestion(color.id, s)"
                                                >
                                                    <span
                                                        class="sku-setup__suggestion-chip"
                                                        :style="{ background: s.hex }"
                                                    />
                                                    <span class="sku-setup__suggestion-name">{{ s.name }}</span>
                                                    <span class="sku-setup__suggestion-code">{{ s.code }}</span>
                                                </button>
                                            </span>
                                        </span>
                                        <!--
                                          The SUPPLIER's own color code,
                                          edited independently. Both
                                          reference suppliers carry one
                                          (SanMar's mainframe code, S&S's
                                          colorCode) and it is what a
                                          purchase order uses. Blank is
                                          legitimate: a free-form color
                                          has no supplier code.
                                        -->
                                        <input
                                            class="sku-setup__input sku-setup__input--code"
                                            :value="color.code"
                                            placeholder="Code"
                                            title="Supplier color code"
                                            :data-testid="`sku-setup-color-code-${color.id}`"
                                            @input="setColorCode(color.id, $event.target.value)"
                                        >
                                    </span>
                                </td>
                                <td
                                    v-for="view in views"
                                    :key="view.code"
                                >
                                    <!--
                                      A REAL upload (GH #3112). This cell
                                      used to hold a button calling
                                      `setImage(..., true)`, which flipped a
                                      BOOLEAN in local state and rendered an
                                      "attached" chip with no file behind
                                      it. Jake: "There needs to be a real
                                      upload function, and the useless
                                      placeholders should go away."

                                      The input is ALWAYS rendered, even
                                      once an image exists, so replacing a
                                      photo is one pick rather than a
                                      remove-then-attach. It is visually
                                      hidden and driven by its label, which
                                      is the only way to style a file input
                                      consistently -- but it is a real
                                      `<input type="file">`, not a facade.
                                    -->
                                    <span class="sku-setup__img-cell">
                                        <span
                                            v-if="hasImage(color.id, view.code)"
                                            class="sku-setup__img-has"
                                        >
                                            <img
                                                class="sku-setup__thumb"
                                                :src="imageUrl(color.id, view.code)"
                                                :alt="`${color.name} ${view.label}`"
                                                :data-testid="`sku-setup-thumb-${color.id}-${view.code}`"
                                            >
                                            <span
                                                class="sku-setup__mono"
                                                :data-testid="`sku-setup-image-${color.id}-${view.code}`"
                                            >{{ view.label }}</span>
                                            <button
                                                type="button"
                                                class="sku-setup__x"
                                                title="Remove image"
                                                :disabled="isUploading(color.id, view.code)"
                                                :data-testid="`sku-setup-remove-image-${color.id}-${view.code}`"
                                                @click="removeImage(color, view.code)"
                                            >
                                                &times;
                                            </button>
                                        </span>
                                        <label
                                            class="sku-setup__btn sku-setup__btn--xs sku-setup__file"
                                            :class="{
                                                'sku-setup__file--off': !skuIsSaved,
                                            }"
                                            :data-testid="`sku-setup-attach-label-${color.id}-${view.code}`"
                                        >
                                            {{ uploadLabel(color.id, view.code) }}
                                            <input
                                                type="file"
                                                class="sku-setup__file-input"
                                                :accept="IMAGE_UPLOAD_ACCEPT"
                                                :disabled="
                                                    isUploading(color.id, view.code) ||
                                                        !skuIsSaved
                                                "
                                                :data-testid="`sku-setup-file-${color.id}-${view.code}`"
                                                @change="uploadImage(color, view.code, $event)"
                                            >
                                        </label>
                                        <!--
                                          THE REASON, not just the disabled
                                          control. Jake found the upload
                                          failing before the SKU existed;
                                          disabling it silently would trade
                                          a confusing failure for a
                                          confusing dead button, which is
                                          the same defect he has already
                                          objected to on the product
                                          editor's unexplained empty
                                          catalog. The cell names the next
                                          action instead.

                                          IT NAMES THE TWO FIELDS, and it
                                          does NOT say "save".

                                          The row is created by the
                                          auto-save, and the server rejects
                                          that save until BOTH `name` and
                                          `item_number` are present
                                          (`validate_required_text` raises
                                          on either being blank). So those
                                          two fields are what the user must
                                          supply -- and saving is not
                                          something they do. It is
                                          debounced and automatic, and this
                                          page has no Save control to
                                          press, so "save the product
                                          first" would send them hunting
                                          for a button that does not exist.

                                          It says "SKU" nowhere on purpose.
                                          Our internal identity is
                                          `supplier_sku` (`NEW-1`), a
                                          DIFFERENT column from the item
                                          number they type, and a word they
                                          have no reason to know.

                                          Stating the consequence -- that
                                          photos come after -- is what
                                          makes the wait legible instead of
                                          looking like a dead control.
                                        -->
                                        <span
                                            v-if="!skuIsSaved"
                                            class="sku-setup__img-gate"
                                            :data-testid="`sku-setup-upload-gate-${color.id}-${view.code}`"
                                        >Add an item number and product name before photos</span>
                                        <!--
                                          A failure is stated IN THE CELL,
                                          not as a toast. The user is
                                          looking at this square; a message
                                          that scrolls away elsewhere is how
                                          a failed upload gets mistaken for
                                          a successful one.
                                        -->
                                        <span
                                            v-if="imageError(color.id, view.code)"
                                            class="sku-setup__img-error"
                                            :data-testid="`sku-setup-image-error-${color.id}-${view.code}`"
                                        >{{ imageError(color.id, view.code) }}</span>
                                    </span>
                                </td>
                                <td>
                                    <!-- Geometry provenance, in the v1
                                         prototype's vocabulary (Jake,
                                         2026-08-27): PRIMARY is where the
                                         definition is drawn, INHERITED
                                         follows it and says WHICH color it
                                         follows, OVERRIDDEN is an explicit
                                         fork (ADR 0071 decision 4).

                                         Three states, matching the three
                                         modes `colorMode()` already
                                         returns -- this names them, it
                                         does not add one. The inherited
                                         case now STATES its provenance as
                                         well as offering the action; it
                                         previously rendered a bare
                                         "Draw here" button and said
                                         nothing about what it inherits. -->
                                    <span
                                        v-if="colorMode(color.id) === 'master'"
                                        class="sku-setup__chip sku-setup__chip--ok"
                                        :data-testid="`sku-setup-geometry-${color.id}`"
                                    >
                                        <span class="sku-setup__chip-dot" /><span
                                            :data-testid="`sku-setup-geometry-state-${color.id}`"
                                        >Primary</span>
                                    </span>
                                    <span
                                        v-else-if="colorMode(color.id) === 'override'"
                                        class="sku-setup__chip sku-setup__chip--warn"
                                        :data-testid="`sku-setup-geometry-${color.id}`"
                                    >
                                        <span class="sku-setup__chip-dot" /><span
                                            :data-testid="`sku-setup-geometry-state-${color.id}`"
                                        >Overridden</span>
                                    </span>
                                    <span
                                        v-else
                                        class="sku-setup__geo-cell"
                                        :data-testid="`sku-setup-geometry-${color.id}`"
                                    >
                                        <span
                                            class="sku-setup__chip"
                                            :data-testid="`sku-setup-geometry-state-${color.id}`"
                                        >
                                            <span class="sku-setup__chip-dot" />Inherited
                                            from {{ masterColorName }}
                                        </span>
                                        <button
                                            type="button"
                                            class="sku-setup__btn sku-setup__btn--xs"
                                            :data-testid="`sku-setup-draw-here-${color.id}`"
                                            @click="setMaster(color.id)"
                                        >
                                            Make primary
                                        </button>
                                    </span>
                                </td>
                                <td class="sku-setup__cell-right">
                                    <button
                                        v-if="canRemoveColor(color.id)"
                                        type="button"
                                        class="sku-setup__x"
                                        title="Remove color"
                                        :data-testid="`sku-setup-remove-color-${color.id}`"
                                        @click="removeColor(color.id)"
                                    >
                                        &times;
                                    </button>
                                </td>
                            </tr>
                            <tr>
                                <td :colspan="views.length + 3">
                                    <button
                                        type="button"
                                        class="sku-setup__btn sku-setup__btn--xs"
                                        data-testid="sku-setup-add-color"
                                        @click="addColor"
                                    >
                                        + Add color
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                <!--
                  4 - Geometry (phase 3c). ADR 0071 decisions 1-5.

                  Two stages side by side, then the color filmstrip that
                  IS the review (decision 5 cut per-color "reviewed"
                  state deliberately -- do not reintroduce it).

                  NOTHING HERE RENDERS A VERDICT ON WHETHER A SCALE IS
                  RIGHT. Decision 3 refuses one, citing #2791: a contrast
                  check that hardcoded `ok: true` in two files and could
                  not fail. The guard is a human reading catalog boxes
                  drawn at true size against a familiar garment, which is
                  what the 1-inch grid and the filmstrip are for.
                -->
                <section
                    class="sku-setup__card"
                    data-testid="sku-setup-section-geometry"
                >
                    <div class="sku-setup__card-head">
                        <span class="sku-setup__secnum">4</span>
                        <h3 class="sku-setup__card-title">
                            Geometry
                        </h3>
                        <!--
                          The instruction for the whole section. It names
                          the RULER (Jake, 2026-08-27) and describes what
                          it can now actually do: it was still teaching the
                          old one-axis limit ("across the garment to move
                          it") after free movement shipped, which is worse
                          than saying nothing -- it sends the reader
                          looking for a constraint that is gone.
                        -->
                        <span
                            class="sku-setup__hint"
                            data-testid="sku-setup-geometry-hint"
                        >
                            Where decoration is allowed, and how big an inch
                            is. Drag the red ruler anywhere onto a dimension
                            you know &mdash; drag its ends to set its
                            length, its body to move it.
                        </span>
                        <span class="sku-setup__spacer" />
                        <label class="sku-setup__grid-toggle">
                            <input
                                v-model="showGrid"
                                type="checkbox"
                                data-testid="sku-setup-grid-toggle"
                            >
                            1&Prime; grid
                        </label>

                        <!--
                          ZOOM. Restored from the v1 prototype's segmented
                          control (design/ge-app.jsx:236), which the v2
                          sweep cut alongside the right-hand panel and the
                          ruler gutters (design/Approach.md:24-27) rather
                          than on its own merits. It pairs with the stage's
                          height cap: the cap makes the section readable,
                          zoom is how detail is recovered when a box needs
                          a closer look.

                          Three steps rather than a slider -- the v1
                          treatment, and enough for "fit / normal / closer"
                          without adding a continuous control to a surface
                          whose complaint was that it had too many.
                        -->
                        <div
                            class="sku-setup__zoom"
                            data-testid="sku-setup-zoom"
                        >
                            <button
                                v-for="step in ZOOM_STEPS"
                                :key="step"
                                type="button"
                                class="sku-setup__zoom-btn"
                                :class="{ 'sku-setup__zoom-btn--on': zoom === step }"
                                :aria-pressed="String(zoom === step)"
                                :data-testid="`sku-setup-zoom-${Math.round(step * 100)}`"
                                @click="zoom = step"
                            >{{ Math.round(step * 100) }}%</button>
                        </div>
                    </div>

                    <!-- Which color is being looked at, and whether it
                         owns its geometry. Stated BEFORE any drag, because
                         decision 5 requires the user to know which of the
                         two they are editing in advance, not after. -->
                    <div
                        class="sku-setup__geo-banner"
                        :class="{ 'sku-setup__geo-banner--forked': viewedColorMode === 'override' }"
                        data-testid="sku-setup-geo-banner"
                    >
                        <template v-if="viewedColorMode === 'override'">
                            <span>
                                {{ viewedColor.name }} &mdash; overridden, no
                                longer following {{ masterColorName }}. Edits
                                here stay on this color.
                            </span>
                            <button
                                type="button"
                                class="sku-setup__mini-btn"
                                data-testid="sku-setup-revert-inherited"
                                @click="revertColorToInherited(viewedColor.id)"
                            >
                                Match {{ masterColorName }} again
                            </button>
                        </template>
                        <template v-else-if="viewedColorMode === 'master'">
                            <span>
                                {{ viewedColor.name }} is primary. Every color
                                that inherits follows these boxes.
                            </span>
                        </template>
                        <template v-else>
                            <span>
                                {{ viewedColor.name }} &mdash; inherited from
                                {{ masterColorName }}. Boxes are read-only
                                here; overriding is an explicit act.
                            </span>
                            <button
                                type="button"
                                class="sku-setup__mini-btn"
                                data-testid="sku-setup-make-different"
                                @click="makeColorDifferent(viewedColor.id)"
                            >
                                Make {{ viewedColor.name }} different
                            </button>
                        </template>
                    </div>

                    <!-- THE PHOTO, not just the fact of one.

                         `photoUrl` and `hasImage` are separate props and
                         BOTH are needed. The stage hands the URL down to
                         `GarmentSilhouette`, which draws an `<img>` when
                         it has one and the vector shape when it does not.
                         Passing only `has-image` told the stage a photo
                         existed while giving it nothing to draw, so every
                         stage fell through to the silhouette -- which is
                         what Jake saw after uploading images.

                         KEYED ON `viewedColor`, NOT THE MASTER. Zone
                         geometry is color-invariant by default; the PHOTO
                         is not. The stage shows one color at a time, so
                         the photo must be that color's own. `imageUrl`
                         returns null for a color with none, and null is
                         the honest answer here: an unimaged color falls
                         back to the silhouette rather than borrowing
                         another color's garment, which would put the
                         right boxes on the wrong blank. -->
                    <!-- A FAILED geometry save, stated where the confirm
                         happened. Silence here would be the worst outcome
                         this surface can produce: the operator's only
                         evidence is the toast, so an unreported failure
                         leaves them believing a measurement is stored. -->
                    <p
                        v-if="geometryError"
                        class="sku-setup__geometry-error"
                        role="alert"
                        data-testid="sku-setup-geometry-error"
                    >{{ geometryError }}</p>
                    <div class="sku-setup__stages">
                        <GeometryStage
                            v-for="view in views"
                            :key="view.code"
                            :view="view"
                            :garment-shape="product.cls"
                            :color="viewedColor"
                            :photo-url="imageUrl(viewedColor.id, view.code)"
                            :has-image="hasImage(viewedColor.id, view.code)"
                            :image-width="product.img.w"
                            :image-height="product.img.h"
                            :zones="zonesFor(view.code)"
                            :zone-dimensions="resolveZoneSize"
                            :offered-zones="offeredZonesFor(view.code)"
                            :line="lineFor(view.code)"
                            :inches="inchesFor(view.code)"
                            :scale-inherited="scaleInheritedFor(view.code)"
                            :show-grid="showGrid"
                            :zoom="zoom"
                            :line-visible="lineVisible"
                            :can-edit="viewedColorMode !== 'inherit'"
                            :color-mode="viewedColorMode"
                            :selected-id="selectedZoneId"
                            :provisional="isProvisional(view.code)"
                            @select-zone="selectedZoneId = $event"
                            @confirm-geometry="confirmGeometry"
                            @add-zone="addZone"
                            @set-line="setLine"
                            @toggle-line-visibility="toggleLineVisibility"
                            @set-inches="setInches"
                            @resize-zone="resizeZone"
                            @move-zone="nudgeZone"
                            @delete-zone="deleteZone"
                            @revert-zone-size="revertZoneSize"
                        />
                    </div>

                    <!-- THE FILMSTRIP IS THE REVIEW. Every color's own
                         photo with the boxes it will actually use. -->
                    <div class="sku-setup__filmstrip-wrap">
                        <div class="sku-setup__hint">
                            <strong>{{ masterColorName }}</strong> is
                            primary. Each thumbnail is that color's own photo
                            with the boxes it will actually use.
                        </div>
                        <div
                            class="sku-setup__filmstrip"
                            data-testid="sku-setup-filmstrip"
                        >
                            <button
                                v-for="color in product.colors"
                                :key="color.id"
                                type="button"
                                class="sku-setup__film-cell"
                                :class="{ 'sku-setup__film-cell--on': color.id === viewedColorId }"
                                :data-testid="`sku-setup-filmstrip-cell-${color.id}`"
                                @click="viewedColorId = color.id"
                            >
                                <!-- THE CELL DRAWS THIS COLOR'S OWN PHOTO. The hint
                                     above the strip promises exactly that, and the
                                     filmstrip is THE REVIEW affordance -- reviewing a
                                     stand-in shape reviews nothing. `imageUrl` returns
                                     null for an unimaged color, which falls back to the
                                     silhouette rather than borrowing another color's
                                     garment. -->
                                <GarmentSilhouette
                                    class="sku-setup__film-art"
                                    :silhouette="product.cls"
                                    :color="color"
                                    view="front"
                                    :photo-url="imageUrl(color.id, 'front')"
                                    @load.capture="onFilmPhotoLoad(color.id, $event)"
                                />
                                <span class="sku-setup__film-name">{{ color.name }}</span>
                                <span class="sku-setup__film-cap">
                                    <span :data-testid="`sku-setup-filmstrip-dims-${color.id}`"
                                    >{{ filmDims(color.id).w }}&times;{{ filmDims(color.id).h }}</span> &middot;
                                    {{ colorModeLabel(color.id) }}
                                </span>
                            </button>
                        </div>
                    </div>
                </section>
                </template>
            </div>
        </div>

        <!-- THE SKU DELETE WARNING. Jake asked for a prominent one, and
             prominence means SPECIFIC: it names the SKU, it names every
             kind of row that goes with it and how many, and it says the
             act cannot be undone.

             The counts come from the SERVER, never from this page's
             state. The rail is still fixture-backed (phase 3d wires it),
             but the rows a delete destroys are real, keyed by the
             `supplier_sku` STRING -- so counting what is on screen would
             report the fixture's colors while the database holds
             geometry this page never loaded. -->
        <div
            v-if="pendingDeleteSku"
            class="sku-setup__danger"
            data-testid="sku-setup-delete-confirm"
        >
            <h3 class="sku-setup__danger-title">
                Delete {{ pendingDeleteSku }}?
            </h3>

            <p
                v-if="deletePreviewLoading"
                data-testid="sku-setup-delete-checking"
            >
                Counting what is stored against this SKU&hellip;
            </p>

            <template v-else>
                <p>
                    This permanently removes <strong>{{ pendingDeleteSku }}</strong>
                    and everything stored against it.
                    <strong>This cannot be undone.</strong>
                </p>
                <ul data-testid="sku-setup-delete-counts">
                    <li
                        v-for="row in deleteCountRows"
                        :key="row.table"
                        :data-testid="`sku-setup-delete-count-${row.table}`"
                    >
                        {{ row.count }} {{ row.label }}
                    </li>
                </ul>
                <p
                    v-if="!deleteCountRows.length"
                    data-testid="sku-setup-delete-nothing-stored"
                >
                    Nothing is stored against this SKU yet, so only the
                    product entry itself is removed.
                </p>
            </template>

            <div class="sku-setup__danger-actions">
                <button
                    v-if="!deletePreviewLoading"
                    type="button"
                    class="sku-setup__btn sku-setup__btn--danger"
                    :disabled="deletingSku"
                    data-testid="sku-setup-delete-confirm-yes"
                    @click="confirmDeleteSku()"
                >
                    {{ deletingSku ? "Deleting..." : `Delete ${pendingDeleteSku}` }}
                </button>
                <button
                    type="button"
                    class="sku-setup__btn"
                    data-testid="sku-setup-delete-confirm-no"
                    @click="cancelDeleteSku()"
                >
                    Cancel
                </button>
            </div>
        </div>

        <div
            v-if="toast"
            class="sku-setup__toast"
            data-testid="sku-setup-toast"
        >
            {{ toast }}
        </div>
    </section>
</template>

<script>
import axios from "@/axios";
// The SINGLE client-side source of truth for the /api/upload_image
// contract, mirrored from the server's own allowlist + cap. Consumed here
// rather than re-declared so this surface cannot drift from the artwork
// pickers -- one contract, one error copy everywhere (GH #3112).
import {
    ARTWORK_UPLOAD_MAX_BYTES,
    ARTWORK_UPLOAD_ERROR_SIZE,
    ARTWORK_UPLOAD_ERROR_EMPTY,
} from "@/utils/artworkUploadContract";
import {
    filterColorSuggestions,
    resolveTypedColor,
} from "@/utils/colorSuggestions";
import GarmentSilhouette from "@/components/Storefront/GarmentSilhouette.vue";
import GeometryStage from "./GeometryStage.vue";
import {
    NUDGE_IN,
    NUDGE_COARSE_IN,
    resolveZoneDimensions,
    revertZoneDimensions,
    lineFromSpec,
    LINE_HORIZONTAL,
    zoneBoxPx,
} from "./zoneGeometry";

// The ten garment shapes the repo's own silhouette corpus renders
// (frontend/src/assets/silhouettes/). Deliberately read from this list
// rather than the handoff bundle's `silhouettes-data.jsx`: the README
// says to use the repo's corpus, whose `bottoms` and `bag` art is drawn
// rather than transcribed.
const GARMENT_SHAPES = [
    "tee",
    "polo",
    "crewneck",
    "hoodie",
    "fullZip",
    "baseballCap",
    "bucketHat",
    "bottoms",
    "bag",
    "generic",
];

// Headwear and bags carry one size; apparel carries the full run.
const APPAREL_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];
const ONE_SIZE = ["OSFA"];
const HEADWEAR_SHAPES = ["baseballCap", "bucketHat"];

/**
 * What this cell accepts, and why it is NARROWER than the shared artwork
 * allowlist.
 *
 * The shared contract also permits .pdf/.ai/.eps/.dst/.emb, which are print
 * and stitch formats. A blank-garment photo is DISPLAYED as an `<img>` on
 * the storefront, in the product editor and behind the geometry stage, so a
 * PDF here would store successfully and then render as a broken image on
 * three surfaces. Narrowing at the picker states the real requirement; the
 * server's own allowlist is still the authority for what may be stored at
 * all.
 *
 * SVG is excluded deliberately as well: it is a vector document, it is the
 * one type the upload route has to sanitize and serve as an attachment, and
 * a garment photo is never legitimately one.
 */
const IMAGE_UPLOAD_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];
const IMAGE_UPLOAD_ACCEPT = IMAGE_UPLOAD_EXTENSIONS.join(",");

/**
 * The reason this file cannot be uploaded, or "" if it can.
 *
 * A CLIENT-SIDE PRE-FLIGHT only -- instant feedback with no wasted round
 * trip. The backend enforces the same contract authoritatively, so this
 * returning "" is not permission, it is just an absence of an obvious
 * problem.
 */
function describeUploadProblem(file) {
    const name = String(file.name || "");
    const dot = name.lastIndexOf(".");
    const extension = dot >= 0 ? name.slice(dot).toLowerCase() : "";
    if (!IMAGE_UPLOAD_EXTENSIONS.includes(extension)) {
        return (
            `Unsupported file type. Allowed: ` +
            `${IMAGE_UPLOAD_EXTENSIONS.join(", ")}.`
        );
    }
    if (file.size > ARTWORK_UPLOAD_MAX_BYTES) {
        return ARTWORK_UPLOAD_ERROR_SIZE;
    }
    // A ZERO-BYTE file is a cloud-sync placeholder that was never
    // materialized to local disk (Google Drive / iCloud / OneDrive
    // Files On-Demand). The picker shows the right name and icon and hands
    // us an empty File. The shared copy names all three services, because
    // naming one sends the other two looking in the wrong place.
    if (file.size === 0) {
        return ARTWORK_UPLOAD_ERROR_EMPTY;
    }
    return "";
}

/** A short, human reason from an axios error -- never "[object Object]". */
function describeRequestError(err) {
    const body = err && err.response && err.response.data;
    if (body && typeof body.error === "string" && body.error) {
        return body.error;
    }
    return (err && err.message) || "please try again.";
}
const SIZE_CLASSES = ["Adult", "Youth"];

/**
 * How long after the last keystroke a save fires.
 *
 * "A few hundred milliseconds after the user stops typing" (Jake,
 * 2026-08-29). 400ms is long enough that ordinary typing coalesces into
 * one request and short enough that a user who types and immediately
 * clicks away is covered by `beforeRouteLeave`'s flush.
 */
const SAVE_DEBOUNCE_MS = 400;

/*
  THE ZONE CATALOG -- printable areas in inches, by slug.

  Fixture-backed like the rest of this phase (3d wires the real API; the
  live source is `placement_zones.default_max_width_in/height_in`, which
  the backend resolver `resolve_zone_dimensions` already reads). Figures
  match the handoff's `GE_ZONES` (design/ge-data.jsx:20-40).

  ADR 0071's own negative consequences apply and are honoured rather than
  papered over: legs, hips and bag panels have NO seeded catalog row.
  Slugs absent from this map resolve to null and are NOT drawn, because
  stamping them at an invented size is "provisional geometry drawn
  confidently" -- the exact failure this feature exists to prevent.
*/
const ZONE_CATALOG = {
    left_chest: { wIn: 4.5, hIn: 4.5, label: "Left Chest" },
    right_chest: { wIn: 4.5, hIn: 4.5, label: "Right Chest" },
    center_chest: { wIn: 12.0, hIn: 12.0, label: "Center Chest" },
    full_back: { wIn: 14.0, hIn: 16.0, label: "Full Back" },
    upper_back: { wIn: 12.0, hIn: 4.0, label: "Upper Back" },
    left_sleeve: { wIn: 3.5, hIn: 12.0, label: "Left Sleeve" },
    right_sleeve: { wIn: 3.5, hIn: 12.0, label: "Right Sleeve" },
    hood_front: { wIn: 8.0, hIn: 3.0, label: "Hood Front" },
    hood_back: { wIn: 8.0, hIn: 4.0, label: "Hood Back" },
    cap_front_panel: { wIn: 5.0, hIn: 2.25, label: "Cap Front Crown" },
    cap_side_panel: { wIn: 3.5, hIn: 2.0, label: "Cap Side Panel" },
    back_closure: { wIn: 2.0, hIn: 1.5, label: "Back Closure" },
    side_band: { wIn: 4.0, hIn: 1.5, label: "Side Band" },
};

/*
  The standard zone set a scale measurement STAMPS, per garment shape
  (handoff `SKU_STANDARD`, design/sku-stage.jsx:12-22). ADR 0071 decision
  2: one measurement yields px_per_inch AND finished geometry to adjust,
  rather than an empty canvas.

  Bottoms and bag zones are deliberately ABSENT from the stamp sets: their
  slugs have no catalog printable area (see ZONE_CATALOG), so stamping
  them would draw boxes at a size nobody chose.
*/
const STANDARD_ZONES = {
    tee: { front: ["left_chest", "center_chest"], back: ["full_back", "upper_back"] },
    polo: { front: ["left_chest"], back: ["full_back"] },
    crewneck: { front: ["left_chest", "center_chest"], back: ["full_back", "upper_back"] },
    hoodie: { front: ["left_chest", "left_sleeve"], back: ["full_back", "upper_back"] },
    fullZip: { front: ["left_chest"], back: ["upper_back", "full_back"] },
    baseballCap: { front: ["cap_front_panel"], side: ["cap_side_panel", "back_closure"] },
    bucketHat: { front: ["cap_front_panel"], side: ["side_band"] },
    bottoms: { front: [], back: [] },
    bag: { front: [], back: [] },
    generic: { front: ["left_chest", "center_chest"], back: ["full_back"] },
};

/*
  The scale line's home position, as a FRACTION of the image box, plus the
  dimension it is most likely measuring. Handoff `SKU_LINE`
  (design/sku-stage.jsx:28-33): a straight, unambiguous edge with no
  decoration zone on top of it -- the hem for tops, the brim for headwear,
  the waistband for bottoms, the base for bags -- so the line and its inch
  field never sit under a box.
*/
const LINE_HOME = {
    apparel: { fromX: 0.24, toX: 0.76, y: 0.86, inches: 22 },
    headwear: { fromX: 0.22, toX: 0.78, y: 0.74, inches: 7 },
    bottoms: { fromX: 0.22, toX: 0.78, y: 0.13, inches: 17 },
    bag: { fromX: 0.2, toX: 0.8, y: 0.82, inches: 15 },
};

function lineFamilyFor(shape) {
    if (HEADWEAR_SHAPES.includes(shape)) return "headwear";
    if (shape === "bottoms") return "bottoms";
    if (shape === "bag") return "bag";
    return "apparel";
}

/** The line's home position in IMAGE pixels for a product. */
function defaultLineFor(product) {
    const home = LINE_HOME[lineFamilyFor(product.cls)];
    // Built through the constructor, like every later mutation, so the
    // stored orientation is an explicit fact from the first render rather
    // than something inferred from coordinates. `lineOrientation` can fall
    // back to inference, but only a line that never went through here
    // would need it.
    return lineFromSpec({
        orientation: LINE_HORIZONTAL,
        position: product.img.h * home.y,
        from: product.img.w * home.fromX,
        to: product.img.w * home.toX,
    });
}

/** Suggested centre for a stamped zone, in IMAGE pixels. Deliberately
 *  coarse: the user nudges. What matters is that the box lands ON the
 *  garment at the right SIZE, which the inch math guarantees. */
function suggestedCentre(product, viewCode, slug, index, total) {
    const w = product.img.w;
    const h = product.img.h;
    // Wearer-relative sidedness: the wearer's LEFT is image-right on a front
    // view (handoff GE_MIRROR), so right_chest is the mirror at 0.38.
    if (slug === "left_chest") return { cx: Math.round(w * 0.62), cy: Math.round(h * 0.36) };
    if (slug === "right_chest") return { cx: Math.round(w * 0.38), cy: Math.round(h * 0.36) };
    if (slug === "left_sleeve") return { cx: Math.round(w * 0.8), cy: Math.round(h * 0.42) };
    if (slug === "right_sleeve") return { cx: Math.round(w * 0.2), cy: Math.round(h * 0.42) };
    if (slug === "upper_back") return { cx: Math.round(w * 0.5), cy: Math.round(h * 0.3) };
    if (slug === "back_closure") return { cx: Math.round(w * 0.7), cy: Math.round(h * 0.55) };
    if (slug === "side_band" || slug === "cap_side_panel") {
        return { cx: Math.round(w * 0.5), cy: Math.round(h * 0.55) };
    }
    if (slug === "cap_front_panel") return { cx: Math.round(w * 0.5), cy: Math.round(h * 0.45) };
    const spread = total > 1 ? 0.42 + index * 0.16 : 0.46;
    return { cx: Math.round(w * 0.5), cy: Math.round(h * spread) };
}

// ADR 0065's size_scale_factor. Youth garments shrink every printable
// area; the number participates in fit maths, so it is a constant here
// rather than a magic literal in the template.
const YOUTH_SCALE_FACTOR = "0.78";
const ADULT_SCALE_FACTOR = "1.00";

// The design's eight-swatch recolor palette.
const PALETTE = [
    "#f6f6f4",
    "#22364f",
    "#26282c",
    "#b3252c",
    "#1f5aa8",
    "#b8bec3",
    "#5f6647",
    "#e2d9c3",
];

/**
 * Zoom steps, as multipliers of the fitted stage.
 *
 * The v1 prototype's 75/100/150 (design/ge-app.jsx:236). Kept as three
 * discrete steps rather than v1's alternative slider: the v2 verdict on
 * v1 was "complicated", and the fix for a stage that is too big should not
 * itself be a continuous control to fiddle with.
 */
const ZOOM_STEPS = [0.75, 1, 1.5];

const DEFAULT_IMG = { w: 1600, h: 1900 };
const HEADWEAR_IMG = { w: 960, h: 700 };

/**
 * Views for a garment shape. Headwear is photographed front + side;
 * everything else front + back. Matches the view vocabulary
 * `placementPreviewGeometry.js` already uses for its hotspot faces, so
 * a geometry row and its photos join on the same words.
 */
function viewsForShape(shape) {
    if (HEADWEAR_SHAPES.includes(shape)) {
        return [
            { code: "front", label: "Front" },
            { code: "side", label: "Side" },
        ];
    }
    return [
        { code: "front", label: "Front" },
        { code: "back", label: "Back" },
    ];
}

function sizeVocabularyForShape(shape) {
    return HEADWEAR_SHAPES.includes(shape) || shape === "bag"
        ? ONE_SIZE
        : APPAREL_SIZES;
}

function imgForShape(shape) {
    return HEADWEAR_SHAPES.includes(shape) ? { ...HEADWEAR_IMG } : { ...DEFAULT_IMG };
}

/**
 * Map one server SKU row onto the page's `product` shape.
 *
 * Phase 3d. This replaced `fixtureProducts()`, which invented two
 * hardcoded PSEUDO-CB SKUs -- the reason every edit on this page used to
 * die on navigation: there was nothing behind them.
 *
 * COLOR IDS ARE DERIVED, NOT STORED. The page keys its per-color state
 * (`state.colors`, `state.images`) by a short slug id, but the SERVER's
 * colour identity is the NAME -- that is what `local_garment_images` and
 * `placement_zone_box_color_overrides` join on. Storing a second id
 * server-side would create a second colour identity that could drift from
 * the one those two tables use. So the id is computed from the name on
 * load and never persisted.
 */
function productFromRow(row) {
    return {
        sku: row.supplier_sku,
        itemNumber: row.item_number || "",
        name: row.name || "",
        cls: row.garment_shape || "tee",
        img: imgForShape(row.garment_shape || "tee"),
        colors: (row.colors || []).map((c) => ({
            id: colorIdFromName(c.color_name),
            name: c.color_name,
            code: c.color_code || "",
            hex: c.hex || "#c9d3db",
        })),
    };
}

/**
 * A stable, URL-safe id for a colour NAME.
 *
 * Deterministic so the same colour keeps the same id across a reload --
 * per-color state keyed by it would otherwise be orphaned every time the
 * page re-hydrated.
 */
function colorIdFromName(name) {
    const slug = String(name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return slug || "color";
}

/**
 * The setup state for a server row: seeded defaults, then the fields the
 * server actually stores laid over them.
 *
 * Geometry is NOT hydrated here. `state.views` and `state.zoneSizes` live
 * in the four geometry tables and have their own endpoints; this phase
 * wires the CATALOG (details, sizes, colours), which is what had no home
 * at all. The geometry half still starts from `seedStateFor` defaults.
 */
function stateFromRow(row, product) {
    const state = seedStateFor(product);
    state.ready = Boolean(row.ready);
    state.sizeClass = row.size_class || "Adult";
    state.sizes = Array.isArray(row.sizes) ? [...row.sizes] : [];
    const master = (row.colors || []).find((c) => c.is_master);
    if (master) state.master = colorIdFromName(master.color_name);
    // Blank-garment photos, from `local_garment_images` (GH #3112). Keyed by
    // the SAME derived colour id the rest of the per-colour state uses, so a
    // reload restores exactly what was uploaded -- which is the difference
    // between this and the boolean it replaced.
    state.images = Object.fromEntries(
        (row.colors || []).map((c) => [
            colorIdFromName(c.color_name),
            { ...(c.images || {}) },
        ])
    );
    return state;
}

/**
 * The page's `product` + `state` folded back into the server's shape.
 *
 * The INVERSE of the two mappers above, and the only place the write
 * payload is built -- so a field added to one side has exactly one place
 * to be added on the other.
 */
function rowFromProduct(product, state) {
    return {
        name: product.name,
        item_number: product.itemNumber,
        garment_shape: product.cls,
        size_class: state.sizeClass,
        sizes: [...(state.sizes || [])],
        ready: Boolean(state.ready),
        /*
          UNNAMED COLOURS ARE OMITTED, not sent empty.

          A colour's name IS its server-side identity -- it is what
          `local_garment_images` and `placement_zone_box_color_overrides`
          join on -- and the backend REQUIRES it
          (`SupplierSkuColor.validate_color_name` raises; the route 400s).

          A new colour now starts with an EMPTY name (Jake, 2026-08-30:
          "since we don't know what the colors may be"), and the page
          auto-saves the whole SKU a few hundred milliseconds after any
          edit. Sending the unnamed row would therefore make every
          subsequent keystroke ANYWHERE on the SKU fail with "Could not
          save" until the colour was named -- a page that looks broken
          because the user has not finished typing.

          Nothing is silently dropped: the row stays on screen, stays
          editable, and saves the moment it has a name. The alternative --
          refusing to add a colour until it is named -- puts the
          validation before the affordance, which is worse.
        */
        colors: (product.colors || [])
            .filter((c) => String(c.name || "").trim() !== "")
            .map((c) => ({
                color_name: c.name,
                color_code: c.code || null,
                hex: c.hex,
                is_master: c.id === state.master,
            })),
    };
}

/**
 * The per-SKU setup state, keyed by SKU. Mirrors the handoff's
 * `store[sku]` shape minus the `views` half, which belongs to the
 * geometry section this phase does not build.
 */
function seedStateFor(product, { ready = false } = {}) {
    const vocabulary = sizeVocabularyForShape(product.cls);
    const colorIds = product.colors.map((c) => c.id);
    return {
        // User-asserted, never computed (ADR 0071 decision 6).
        ready,
        sizeClass: "Adult",
        sizes:
            vocabulary.length === 1
                ? [...vocabulary]
                : vocabulary.slice(0, 6),
        // Where geometry is drawn. Null when there are no colors.
        master: colorIds[0] || null,
        /*
          EMPTY, not `{front: true, back: true}` (GH #3112).

          The old seed asserted every colour already had a photo for every
          view -- which was harmless only while the value was a meaningless
          boolean. Now that the value is a STORED image, seeding it true
          would make a brand-new SKU claim images that do not exist, hide
          the gap line that says so, and mark geometry non-provisional on
          the strength of photos nobody uploaded. Hydrated from the server
          by `stateFromRow`.
        */
        images: Object.fromEntries(colorIds.map((id) => [id, {}])),
        colors: Object.fromEntries(
            colorIds.map((id) => [
                id,
                {
                    mode: id === colorIds[0] ? "master" : "inherit",
                    // A forked color's OWN boxes, keyed by view. Null
                    // while it inherits -- the common case stores nothing,
                    // same as the backend override table.
                    zones: null,
                },
            ])
        ),
        // Per-view geometry: the scale line, its measured inches, whether
        // the number is this view's own or ridden from the first view, and
        // the color-invariant zone boxes every inheriting color uses.
        views: Object.fromEntries(
            viewsForShape(product.cls).map((v, i) => [
                v.code,
                {
                    line: defaultLineFor(product),
                    inches: "",
                    // The FIRST view owns the number; every later view
                    // RIDES it until someone measures that view itself
                    // (ADR 0071 decision 2 -- "same shoot, same framing,
                    // one number"). Seeding every view as 'own' would
                    // make the second view claim a measurement nobody
                    // took, which is the silent-wrongness this feature
                    // exists to prevent.
                    ppiSrc: i === 0 ? "own" : "inherit",
                    zones: [],
                    /*
                      PROVISIONAL: this view's boxes were authored on the
                      generic silhouette rather than a photo of the blank,
                      and nobody has confirmed them against a real one.

                      STORED, not derived -- and the choice is the whole
                      point. `!hasImage` reads what is on screen NOW;
                      this records HOW THE GEOMETRY CAME TO BE, which is
                      history and therefore not recoverable from current
                      state. Deriving it would clear the warning the
                      instant a photo was attached, and Jake ruled
                      exactly that out: a photo arriving does not mean
                      anyone re-checked the boxes.

                      Its invalidation rules are small and total, which
                      is what makes a stored flag safe here: set when
                      geometry is authored with no photo present
                      (`setInches`, `addZone`), cleared ONLY by an
                      explicit human confirm (`confirmGeometry`), and
                      discarded with the geometry it describes on a shape
                      change (ADR 0071 decision 7).
                    */
                    provisional: false,
                },
            ])
        ),
        /*
          PER-GARMENT printable-area overrides, keyed `view:slug`.

          JAKE'S RULE, 2026-08-26: "if you change a zone on the 'master'
          that should be inherited by all of the other colors' zones that
          are inherited. The master shouldn't be made into an exception."

          NOTE THE KEY: there is NO COLOR IN IT. A zone's printable area
          in inches is a property of the GARMENT (a tee's left chest is
          4.5in wide in White and in Navy), so a corner drag cannot be
          recorded against the color that happens to be on screen. That
          is the bug the rule forbids: an edit that looked global silently
          applying to one color. The backend table has no `color_name`
          column for the same reason -- the write is unrepresentable, not
          merely discouraged.

          Per-COLOR forking is a different grain and lives in
          `colors[id].zones` above: it forks the BOX, not the inches.
        */
        zoneSizes: {},
    };
}

export default {
    name: "SkuSetup",
    components: { GarmentSilhouette, GeometryStage },
    props: {
        /**
         * The supplier whose SKUs this page sets up, from
         * `/admin/suppliers/non-integrated/:supplierId/sku-setup`
         * (`props: true` on the route). A STRING, because route params
         * always are -- compared and interpolated, never arithmetic.
         *
         * Jake's IA decision, 2026-08-26: a SKU lives under a supplier.
         * Before this the page assumed a single implicit supplier and
         * printed its name as a literal.
         */
        supplierId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        // EMPTY until `loadProducts()` hydrates from the server. The page
        // used to seed itself from `fixtureProducts()`, which is why every
        // edit died on navigation -- there was no row behind it.
        const products = [];
        const store = {};
        return {
            GARMENT_SHAPES,
            SIZE_CLASSES,
            PALETTE,
            ZOOM_STEPS,
            products,
            store,
            selectedSku: "",
            // Which color the stages are showing. Distinct from `master`
            // (where geometry is drawn): you can LOOK at an inheriting
            // color without forking it.
            viewedColorId: "",
            // Hydration state for the rail. `loading` gates the editor so
            // it cannot render against an empty product before the fetch
            // lands; `loadError` is shown INSTEAD of an empty rail, which
            // would otherwise read as "this supplier has no SKUs".
            loading: true,
            loadError: "",
            /**
             * Auto-save bookkeeping.
             *
             * `saveState` drives the indicator: '' | 'saving' | 'saved' |
             * 'error'. `saveError` carries the SERVER's reason -- a
             * generic "could not save" would leave a superadmin with no
             * idea which field the backend refused.
             *
             * `saveSeq` is the STALE-RESPONSE GUARD. Every save takes the
             * next number; a response is applied only if it is still the
             * newest. Without it a slow save landing after a newer edit
             * would overwrite the user's newer text with older server
             * state, and they would watch their typing revert for no
             * visible reason.
             *
             * `knownSkus` records which SKUs the server has confirmed, so
             * a 201 is not re-sent as a create on the next keystroke.
             */
            saveState: "",
            saveError: "",
            saveTimer: null,
            saveSeq: 0,
            lastAppliedSeq: 0,
            pendingSku: "",
            knownSkus: [],
            /*
              Geometry confirm is a SEPARATE write from the debounced SKU
              auto-save, with its own state: it targets a different route,
              is triggered by an explicit act rather than by typing, and
              its failure must not be overwritten by an unrelated field
              save reporting "Saved" a moment later.
            */
            geometryError: "",
            geometrySeq: 0,
            geometryAppliedSeq: 0,
            selectedZoneId: null,
            showGrid: false,
            /**
             * In-progress color-name text, keyed by color id, for rows
             * being edited right now. A row is absent from this map
             * unless it is mid-edit.
             *
             * This map is what makes a two-word color name typable. The
             * committed `color.name` is the CANONICAL identity key; the
             * draft is the raw keystrokes. Binding the field straight to
             * the canonical value is what ate the space in
             * "Athletic Heather" -- see `draftColorName`.
             */
            colorDrafts: {},
            /**
             * Stage magnification, and whether the scale line is drawn.
             * Both are VIEW PREFERENCES for the whole section -- they say
             * nothing about the geometry and are deliberately not part of
             * per-view state, so neither can be mistaken for a measurement
             * or be lost when a view is recalibrated. Restored from the v1
             * prototype's segmented control (design/ge-app.jsx:236).
             */
            zoom: 1,
            lineVisible: true,
            // Filled by `loadSupplier()` from the route's supplier id.
            // Starts as a neutral placeholder rather than a supplier
            // NAME, so a failed fetch cannot leave a page claiming to be
            // scoped to a supplier it never resolved.
            supplierLine: "Non-integrated supplier",
            toast: "",
            toastTimer: null,
            // The SKU whose delete is being confirmed, or null.
            pendingDeleteSku: null,
            // Per-table row counts from the SERVER. Never derived from
            // this page's fixtures -- see the confirm panel's comment.
            deleteCounts: null,
            deleteLabels: {},
            deletePreviewLoading: false,
            deletingSku: false,
            /*
              Per-cell upload state, keyed `colorId:viewCode` (GH #3112).

              SEPARATE from `state.images`, deliberately. `state.images` is
              what the SERVER stores; these two are transient UI facts about
              a request in flight. Folding them together is how an "attached"
              chip ends up on screen for a request that failed -- the exact
              defect this feature exists to remove.
            */
            imageBusy: {},
            /**
             * Decoded size of each filmstrip cell's photo, keyed by color id.
             *
             * NOT PERSISTED, for the same reason `GeometryStage.photoNatural` is not:
             * these are a property of the image file, re-derivable on every load, so
             * storing them would add state that can go stale against the thing it
             * describes. The stage keeps its own copy rather than sharing this one --
             * it renders the VIEWED color and this map spans every cell, so folding
             * them together would couple two components to one another's lifecycle for
             * no gain.
             */
            filmNatural: {},
            imageErrors: {},
            // Exposed for the file input's `accept`.
            IMAGE_UPLOAD_ACCEPT,
        };
    },
    computed: {
        /**
         * The per-table counts, as rows to print, NON-ZERO ONLY.
         *
         * Zero rows are dropped rather than shown as "0 garment images":
         * a warning padded with zeroes reads as boilerplate, and the
         * point of naming counts is that the numbers are real.
         */
        deleteCountRows() {
            const counts = this.deleteCounts || {};
            return Object.keys(counts)
                .filter((table) => counts[table] > 0)
                .map((table) => ({
                    table,
                    count: counts[table],
                    label: this.deleteLabels[table] || table,
                }));
        },
        /**
         * The selected product, or an EMPTY placeholder.
         *
         * The placeholder matters: before hydration and when a supplier
         * genuinely has no SKUs there is no product, and every computed
         * below dereferences `product.colors`. Returning a shaped empty
         * object keeps those total rather than scattering `?.` through
         * the template -- and the editor is gated on `hasProduct` anyway,
         * so the placeholder is never what the user sees.
         */
        product() {
            return (
                this.products.find((p) => p.sku === this.selectedSku) ||
                this.products[0] || {
                    sku: "",
                    itemNumber: "",
                    name: "",
                    cls: "tee",
                    img: { ...DEFAULT_IMG },
                    colors: [],
                }
            );
        },
        state() {
            return this.store[this.selectedSku] || seedStateFor(this.product);
        },
        /** Whether there is a real SKU to edit. Gates the editor. */
        hasProduct() {
            return Boolean(this.product.sku);
        },
        /**
         * Does the SELECTED key name a product that is actually in the list?
         *
         * Distinct from `hasProduct`, which reads `product.sku` -- and
         * `product` falls back to `products[0]`, so it stays truthy whenever
         * the supplier has ANY product regardless of what is selected. That
         * fallback is right for keeping the computeds total, but it cannot
         * answer "has the user chosen one", which is the question the two
         * empty states differ on.
         */
        hasSelection() {
            return this.products.some((p) => p.sku === this.selectedSku);
        },
        /**
         * Is the SELECTED SKU one the SERVER has, rather than a local draft?
         *
         * THE PRECONDITION FOR ATTACHING A PHOTO, and it is not "an item
         * number has been typed". The attach PUTs to
         * `/skus/<selectedSku>/images`, whose `_sku_or_404` resolves a
         * PERSISTED `supplier_skus` row -- so what the picker depends on is
         * that the row EXISTS, not that a field on screen is non-empty.
         *
         * Those come apart in the ordinary create flow. `addProduct` mints a
         * local `NEW-n` key and deliberately does NOT save, because the
         * server requires `name` AND `item_number` (both `nullable=False`
         * with validators) and an immediate save would paint a rejection
         * across a form the user has not begun filling in. So a typed item
         * number alone does not create the row, and gating on the typed
         * field would re-enable the picker while the upload was still bound
         * to 404 -- the same broken upload with a later start time.
         *
         * `knownSkus` is already the list the server has confirmed: seeded
         * from the load and appended on each successful save. Reusing it
         * keeps ONE answer to "does this SKU exist server-side" rather than
         * adding a second that could disagree with it.
         */
        skuIsSaved() {
            return this.knownSkus.includes(this.selectedSku);
        },
        /** The indicator's text. '' renders nothing at all. */
        saveStateLabel() {
            if (this.saveState === "saving") return "Saving...";
            if (this.saveState === "saved") return "Saved";
            if (this.saveState === "error") {
                // Never the word "saved" on this branch -- the whole point
                // is that the user must not believe their work is stored.
                // Deliberately avoids the word "saved" ENTIRELY, rather
                // than relying on the "Not " prefix to negate it. A
                // glancing read of "Not saved" can register as "saved",
                // and this is the one label where that misread costs the
                // user their work.
                return this.saveError
                    ? `Could not save -- ${this.saveError}`
                    : "Could not save -- your change is not stored.";
            }
            return "";
        },
        views() {
            return viewsForShape(this.product.cls);
        },
        offeredSizeVocabulary() {
            return sizeVocabularyForShape(this.product.cls);
        },
        sizeScaleFactor() {
            return this.state.sizeClass === "Youth"
                ? YOUTH_SCALE_FACTOR
                : ADULT_SCALE_FACTOR;
        },
        /**
         * The color the stages are SHOWING. Falls back to the master when
         * the viewed color was removed, so the stage never renders against
         * an id that no longer exists.
         */
        viewedColor() {
            return (
                this.product.colors.find((c) => c.id === this.viewedColorId) ||
                this.product.colors.find((c) => c.id === this.state.master) ||
                this.product.colors[0] || { id: "", name: "", hex: "#c9d3db" }
            );
        },
        viewedColorMode() {
            return this.colorMode(this.viewedColor.id);
        },
        masterColorName() {
            const master = this.product.colors.find(
                (c) => c.id === this.state.master
            );
            return master ? master.name : "the definition";
        },
        /** Views that carry a usable scale -- what the state strip reports. */
        scaledViewCount() {
            return this.views.filter((v) => this.pxPerInchFor(v.code)).length;
        },
        gaps() {
            return this.gapsFor(this.product);
        },
        markReadyLabel() {
            if (this.state.ready) return "Ready ✓ · unmark";
            // "Mark ready anyway" when gaps exist: the button is never
            // disabled, so the copy is what acknowledges the gaps.
            return this.gaps.length ? "Mark ready anyway" : "Mark ready";
        },
        stateStrip() {
            const p = this.product;
            const s = this.state;
            const detailsDone = Boolean(p.name && p.itemNumber);
            const imaged = p.colors.filter((c) =>
                this.views.every((v) => this.hasImage(c.id, v.code))
            ).length;
            return [
                {
                    key: "details",
                    label: "Details",
                    done: detailsDone,
                    detail: detailsDone ? "complete" : "needs a name",
                },
                {
                    key: "sizes",
                    label: "Sizes",
                    done: s.sizes.length > 0,
                    detail: s.sizes.length ? `${s.sizes.length} offered` : "none",
                },
                {
                    key: "colors",
                    label: "Colors",
                    done: p.colors.length > 0 && imaged === p.colors.length,
                    detail: p.colors.length
                        ? `${imaged} with images`
                        : "none",
                },
                {
                    // Real as of phase 3c: a view counts once it carries a
                    // usable scale. Deliberately counts SCALE, not boxes --
                    // a view with boxes and no px_per_inch cannot render a
                    // decoration at a real size, so it is not done.
                    key: "geometry",
                    label: "Geometry",
                    done:
                        this.views.length > 0 &&
                        this.scaledViewCount === this.views.length,
                    detail: `${this.scaledViewCount} of ${this.views.length} views`,
                },
            ];
        },
    },
    beforeUnmount() {
        if (this.toastTimer) clearTimeout(this.toastTimer);
    },
    mounted() {
        this.loadSupplier();
        this.loadProducts();
    },
    beforeRouteLeave(to, from, next) {
        // Flush a pending debounce before navigating away. Without this,
        // a user who types and immediately clicks a link loses the edit --
        // which is the very bug this feature fixes, just in a narrower
        // window.
        this.flushPendingSave();
        next();
    },
    methods: {
        // --- hydration + auto-save (phase 3d) ------------------------

        /**
         * Load this supplier's SKUs. THE READ HALF of the fix.
         *
         * Replaces `fixtureProducts()`. A failure is SHOWN, not
         * swallowed: an empty rail after a failed fetch is
         * indistinguishable from a supplier that genuinely has no SKUs,
         * and the difference matters -- one invites "add a SKU", the
         * other means "your data did not load".
         */
        async loadProducts() {
            if (!this.supplierId) {
                this.loading = false;
                return;
            }
            this.loading = true;
            this.loadError = "";
            try {
                const resp = await axios.get(
                    `/api/suppliers/non-integrated/${this.supplierId}/skus`
                );
                const rows = resp.data?.skus || [];
                const products = [];
                const store = {};
                for (const row of rows) {
                    const product = productFromRow(row);
                    products.push(product);
                    store[product.sku] = stateFromRow(row, product);
                }
                this.products = products;
                this.store = store;
                this.knownSkus = products.map((p) => p.sku);
                if (products.length) {
                    this.selectedSku = products[0].sku;
                    this.viewedColorId = (products[0].colors[0] || {}).id || "";
                }
            } catch (err) {
                this.loadError =
                    err?.response?.data?.error ||
                    "Could not load this supplier's SKUs.";
            } finally {
                this.loading = false;
            }
        },

        /**
         * Queue an auto-save for the CURRENT SKU.
         *
         * Called by every mutation below. Debounced rather than
         * save-on-blur or an explicit button (Jake's decision,
         * 2026-08-29) -- note this deliberately DIFFERS from the product
         * editor next door, which has an explicit "Save product".
         *
         * Keyed on `pendingSku` rather than reading `selectedSku` at fire
         * time: a user who edits SKU A and immediately selects SKU B must
         * have A's edit saved to A, not written onto B.
         */
        queueSave() {
            if (!this.hasProduct) return;
            this.pendingSku = this.selectedSku;
            this.saveState = "saving";
            this.saveError = "";
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                this.saveTimer = null;
                this.savePending();
            }, SAVE_DEBOUNCE_MS);
        },

        /** Fire a queued save immediately, if one is waiting. */
        flushPendingSave() {
            if (!this.saveTimer) return;
            clearTimeout(this.saveTimer);
            this.saveTimer = null;
            this.savePending();
        },

        /**
         * PUT the pending SKU. THE WRITE HALF of the fix.
         *
         * Sends the WHOLE SKU, which is what makes PUT the right verb: the
         * page always holds complete state, so the write is idempotent and
         * a retry cannot half-apply.
         *
         * The `seq` guard is the stale-response protection. A response is
         * allowed to touch `saveState` only while it is still the newest
         * request in flight -- otherwise a slow save landing after a newer
         * edit would report "Saved" for state the user has already changed,
         * or worse, an old failure would mark a good save as failed.
         *
         * NOTE what this deliberately does NOT do: apply the response body
         * back onto the page. The server echoes the row it stored, but by
         * the time it lands the user may have typed further, and writing
         * the echo back would revert their newer text. The local state is
         * authoritative between saves; the server is authoritative only on
         * reload.
         */
        async savePending() {
            const sku = this.pendingSku || this.selectedSku;
            const product = this.productFor(sku);
            const state = this.store[sku];
            if (!product || !state) return;

            const seq = ++this.saveSeq;
            const body = rowFromProduct(product, state);

            try {
                await axios.put(
                    `/api/suppliers/non-integrated/${this.supplierId}` +
                        `/skus/${sku}`,
                    body
                );
                if (seq < this.lastAppliedSeq) return;
                this.lastAppliedSeq = seq;
                if (!this.knownSkus.includes(sku)) this.knownSkus.push(sku);
                this.saveState = "saved";
                this.saveError = "";
            } catch (err) {
                if (seq < this.lastAppliedSeq) return;
                this.lastAppliedSeq = seq;
                // VISIBLE failure, carrying the server's own reason. A
                // silent auto-save failure is worse than no auto-save:
                // the user believes their work is stored and navigates
                // away from it.
                this.saveState = "error";
                this.saveError =
                    err?.response?.data?.error ||
                    "The change could not be saved.";
            }
        },

        /**
         * Resolve the supplier named in the route, for the header only.
         *
         * DELIBERATELY NON-FATAL. The SKU state on this page is still
         * fixture-backed (phase 3d wires it), so a failed supplier fetch
         * must not blank a page whose real content does not depend on
         * it. The header falls back to the neutral "Non-integrated
         * supplier" it starts with, which is true but unspecific --
         * better than printing a supplier name we did not resolve.
         *
         * When 3d lands, the SKU list becomes supplier-scoped and this
         * becomes load-bearing; at that point the failure should surface
         * as `sku-setup-error` rather than being swallowed.
         */
        async loadSupplier() {
            if (!this.supplierId) return;
            try {
                const resp = await axios.get(
                    `/api/suppliers/non-integrated/${this.supplierId}`
                );
                const supplier = resp.data?.supplier;
                if (supplier?.name) {
                    this.supplierLine = supplier.name;
                }
            } catch {
                // Header keeps its neutral placeholder. See above.
            }
        },
        // --- SKU deletion --------------------------------------------

        /**
         * Open the warning. DOES NOT DELETE.
         *
         * Fetches the per-table counts first, because the whole point of
         * the warning is that it names what goes -- and this page cannot
         * know that. Its rail is fixtures; the rows are in the database.
         */
        async startDeleteSku(sku) {
            const target = sku || this.selectedSku;
            this.pendingDeleteSku = target;
            this.deleteCounts = null;
            this.deleteLabels = {};
            this.deletePreviewLoading = true;
            try {
                const resp = await axios.get(
                    `/api/supplier-skus/${target}/delete-preview`
                );
                this.deleteCounts = resp.data?.counts || {};
                this.deleteLabels = resp.data?.labels || {};
            } catch {
                // Counts unavailable. The confirm still offers the
                // delete -- refusing outright would make an unreachable
                // preview route into an inability to clean up at all --
                // but it must not imply "nothing is stored", so the
                // counts stay null and the panel says so.
                this.deleteCounts = null;
                this.deleteLabels = {};
            } finally {
                this.deletePreviewLoading = false;
            }
        },
        cancelDeleteSku() {
            this.pendingDeleteSku = null;
            this.deleteCounts = null;
            this.deleteLabels = {};
            this.deletePreviewLoading = false;
        },
        /**
         * Perform the delete, then drop the SKU from the rail.
         *
         * The rail row is removed only AFTER the server confirms. An
         * optimistic removal would tell a superadmin the geometry was
         * gone while every row was still in the database -- and with no
         * SKU row to find them from, those rows are unreachable.
         */
        async confirmDeleteSku() {
            const sku = this.pendingDeleteSku;
            if (!sku) return;
            this.deletingSku = true;
            try {
                const resp = await axios.delete(`/api/supplier-skus/${sku}`);
                const removed = resp.data?.total;

                const idx = this.products.findIndex((p) => p.sku === sku);
                if (idx !== -1) this.products.splice(idx, 1);
                delete this.store[sku];

                // Never leave a dangling selection -- it would render an
                // editor for a product that no longer exists.
                if (this.selectedSku === sku && this.products.length) {
                    this.selectedSku = this.products[0].sku;
                    this.viewedColorId =
                        (this.products[0].colors[0] || {}).id || "";
                }

                this.pendingDeleteSku = null;
                this.deleteCounts = null;
                this.showToast(
                    `${sku} deleted` +
                        (typeof removed === "number"
                            ? ` — ${removed} associated row` +
                              (removed === 1 ? "" : "s") + " removed."
                            : ".")
                );
            } catch (err) {
                this.showToast(
                    err?.response?.data?.error ||
                        `${sku} could not be deleted — nothing was removed.`
                );
            } finally {
                this.deletingSku = false;
            }
        },

        stateFor(sku) {
            return this.store[sku];
        },
        productFor(sku) {
            return this.products.find((p) => p.sku === sku);
        },
        selectSku(sku) {
            // Flush FIRST: a pending edit belongs to the SKU that was
            // selected when it was made, and `savePending` reads
            // `pendingSku` rather than `selectedSku` for the same reason.
            this.flushPendingSave();
            this.selectedSku = sku;
            this.saveState = "";
            this.saveError = "";
        },
        /*
          THE AUTO-SAVE SEAM. Every mutation on this page funnels through
          these two setters, so queueing the save here covers all of them
          -- details, sizes, colours, master, ready -- rather than
          sprinkling a save call through forty methods where the next new
          one would forget it.

          Geometry mutations also pass through `setState` (via
          `setViewState`) and so queue a save too. That write is currently
          a no-op server-side: the catalog endpoint stores details, sizes
          and colours, while `state.views` / `state.zoneSizes` belong to
          the four geometry tables, which have their own write path. The
          save is harmless and keeps ONE seam rather than two competing
          ones; wiring geometry through is the next slice.
        */
        setState(patch) {
            this.store[this.selectedSku] = {
                ...this.store[this.selectedSku],
                ...patch,
            };
            this.queueSave();
        },
        setProduct(patch) {
            const idx = this.products.findIndex(
                (p) => p.sku === this.selectedSku
            );
            if (idx === -1) return;
            this.products[idx] = { ...this.products[idx], ...patch };
            this.queueSave();
        },
        showToast(message) {
            this.toast = message;
            if (this.toastTimer) clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => {
                this.toast = "";
            }, 4000);
        },

        // --- geometry (section 4, phase 3c) --------------------------

        /**
         * A zone's printable area IN INCHES on THIS garment.
         *
         * Passed into both stages as a function so the resolution lives in
         * ONE place rather than being recomputed per stage. Delegates to
         * `resolveZoneDimensions`, which mirrors the backend resolver's
         * precedence exactly: this garment's override, else the catalog,
         * else null.
         *
         * NOTE the arguments: view and slug, NO COLOR. See `zoneSizes`.
         */
        resolveZoneSize(viewCode, slug) {
            return resolveZoneDimensions({
                overrides: this.state.zoneSizes,
                catalog: ZONE_CATALOG,
                viewCode,
                zoneSlug: slug,
            });
        },
        viewState(viewCode) {
            const existing = (this.state.views || {})[viewCode];
            if (existing) return existing;
            const isPrimary = this.views.length
                ? this.views[0].code === viewCode
                : true;
            return {
                line: defaultLineFor(this.product),
                inches: "",
                ppiSrc: isPrimary ? "own" : "inherit",
                zones: [],
                provisional: false,
            };
        },
        setViewState(viewCode, patch) {
            this.setState({
                views: {
                    ...this.state.views,
                    [viewCode]: { ...this.viewState(viewCode), ...patch },
                },
            });
        },
        lineFor(viewCode) {
            return this.viewState(viewCode).line;
        },
        /**
         * The inches shown on a view's field.
         *
         * A view that has not been measured RIDES the first view's number
         * (ADR 0071 decision 2 -- "same shoot, same framing, one number"),
         * and says so via `scaleInheritedFor`. It stops riding the moment
         * someone measures it, which is what `ppiSrc: 'own'` records.
         */
        inchesFor(viewCode) {
            const own = this.viewState(viewCode);
            if (own.inches !== "" || own.ppiSrc === "own") return own.inches;
            const primary = this.views[0];
            return primary ? this.viewState(primary.code).inches : "";
        },
        /**
         * Does this view carry provisional geometry?
         *
         * A STORED read, deliberately -- see the field's comment in
         * `seedStateFor`. Guarded by "are there boxes at all" so a view
         * whose geometry was cleared cannot keep warning about it.
         */
        isProvisional(viewCode) {
            const view = this.viewState(viewCode);
            return Boolean(view.provisional) && this.zonesFor(viewCode).length > 0;
        },
        /**
         * The human confirm -- the ONLY thing that clears provisional.
         *
         * Refuses without a photo rather than trusting the caller: with no
         * image there is nothing to check the boxes against, so a confirm
         * would assert something nobody could have seen. The stage does
         * not render the button in that state either; this is the guard
         * behind the affordance, not a duplicate of it.
         */
        confirmGeometry({ viewCode }) {
            if (!this.hasImage(this.viewedColorId, viewCode)) return;
            this.persistGeometry(viewCode);
        },
        /**
         * The canvas rect to store for one view, in the page's frame.
         *
         * ONE ROW PER (SKU, VIEW), because that is what the table's
         * `uq_placement_zone_boxes_sku_view` says the identity is, and
         * what `resolve_zone_geometry` reads back. The stored rect is the
         * UNION of the view's zones — the decoratable canvas, inside
         * which `compute_decoration_rect` centres a decoration — rather
         * than any single zone, because a garment measured with a left
         * chest AND a full front has one canvas containing both.
         *
         * Authored in `product.img` space, deliberately. Every centre the
         * page holds (`suggestedCentre`) and the ruler `px_per_inch` is
         * derived from (`pxPerInchFor`) live in that declared frame, so
         * the box and its scale are self-consistent. The stage's photo
         * pixel space is a RENDERING of this one (`canvasScale`), not the
         * space these numbers are recorded in.
         *
         * Returns null when there is nothing usable to send, which is the
         * client half of the endpoint's refusal: no scale, or no zone to
         * bound. Posting a box the server is certain to reject would show
         * the operator a failure for work they have not finished.
         */
        geometryBoxFor(viewCode) {
            const pxPerInch = this.pxPerInchFor(viewCode);
            if (!(pxPerInch > 0)) return null;

            const rects = this.zonesFor(viewCode)
                .map((zone) => {
                    const dims = this.resolveZoneSize(viewCode, zone.slug);
                    if (!dims) return null;
                    return zoneBoxPx(
                        {
                            cx: zone.cx,
                            cy: zone.cy,
                            wIn: dims.wIn,
                            hIn: dims.hIn,
                        },
                        pxPerInch
                    );
                })
                .filter(Boolean);
            if (!rects.length) return null;

            const frame = this.product.img || {};
            if (!(frame.w > 0) || !(frame.h > 0)) return null;
            const left = Math.min(...rects.map((r) => r.x));
            const top = Math.min(...rects.map((r) => r.y));
            const right = Math.max(...rects.map((r) => r.x + r.w));
            const bottom = Math.max(...rects.map((r) => r.y + r.h));

            return {
                view_code: viewCode,
                group_name: viewCode,
                image_width_px: frame.w,
                image_height_px: frame.h,
                x_px: left,
                y_px: top,
                w_px: right - left,
                h_px: bottom - top,
                px_per_inch: pxPerInch,
            };
        },
        /**
         * Send one view's geometry, and REPORT WHAT HAPPENED.
         *
         * THE BUG THIS FIXES. This handler used to clear `provisional`
         * and toast "geometry confirmed against the photo" having made
         * no request at all. The operator measured a garment, saw a
         * success message, and lost the work on reload — and
         * `px_per_inch`, the one datum a decorated preview cannot be
         * rendered without, was computed by `pxPerInchFor` a few lines
         * away and discarded.
         *
         * A silent failure here is worse than no save, because the toast
         * is the operator's only evidence: it would confirm a measurement
         * that is not stored. So the failure path clears nothing, says so
         * visibly, and carries the SERVER's own reason.
         *
         * The `seq` guard is the same stale-response protection
         * `savePending` uses, for the same reason — a slow confirm
         * landing after a newer one must not report on state the user has
         * already changed.
         */
        async persistGeometry(viewCode) {
            const box = this.geometryBoxFor(viewCode);
            if (!box) return;
            if (!this.skuIsSaved) {
                this.geometryError =
                    "Save this SKU before confirming its geometry.";
                return;
            }

            const sku = this.selectedSku;
            const seq = ++this.geometrySeq;
            this.geometryError = "";
            try {
                await axios.post(this.geometryUrl(sku), box);
                if (seq < this.geometryAppliedSeq) return;
                this.geometryAppliedSeq = seq;
                this.setViewState(viewCode, { provisional: false });
                this.showToast(
                    `${viewCode} geometry confirmed against the photo — ` +
                        "no longer provisional."
                );
            } catch (err) {
                if (seq < this.geometryAppliedSeq) return;
                this.geometryAppliedSeq = seq;
                // NOTHING is cleared: the view stays provisional,
                // because it genuinely still is.
                this.geometryError =
                    `Could not save geometry — ${describeRequestError(err)}`;
            }
        },
        /** The geometry route for one SKU under this supplier. */
        geometryUrl(sku) {
            return (
                `/api/suppliers/non-integrated/${this.supplierId}` +
                `/skus/${encodeURIComponent(sku)}/placement_zone_boxes`
            );
        },
        scaleInheritedFor(viewCode) {
            const primary = this.views[0];
            if (!primary || primary.code === viewCode) return false;
            const own = this.viewState(viewCode);
            return own.ppiSrc !== "own" && own.inches === "";
        },
        pxPerInchFor(viewCode) {
            const inches = Number(this.inchesFor(viewCode));
            if (!(inches > 0)) return null;
            const line = this.lineFor(viewCode);
            const px = Math.max(
                Math.abs(line.b.x - line.a.x),
                Math.abs(line.b.y - line.a.y)
            );
            return px > 0 ? px / inches : null;
        },
        /**
         * The boxes the CURRENTLY VIEWED color will actually use.
         *
         * A color in `override` mode reads its OWN boxes; every other
         * color reads THROUGH to the color-invariant set. Reading
         * through is what makes a later master edit reach them with no
         * per-color rewrite (Jake's rule) -- the inherited colors are
         * not carrying a copy that could go stale.
         */
        zonesFor(viewCode) {
            const color = this.state.colors[this.viewedColor.id];
            if (color && color.mode === "override" && color.zones) {
                return color.zones[viewCode] || [];
            }
            return this.viewState(viewCode).zones;
        },
        offeredZonesFor(viewCode) {
            const standard = (STANDARD_ZONES[this.product.cls] || {})[viewCode] || [];
            return standard
                .map((slug) => {
                    const dims = this.resolveZoneSize(viewCode, slug);
                    if (!dims) return null;
                    return {
                        slug,
                        label: (ZONE_CATALOG[slug] || {}).label || slug,
                        wIn: dims.wIn,
                        hIn: dims.hIn,
                    };
                })
                .filter(Boolean);
        },
        makeZone(viewCode, slug, index, total) {
            const centre = suggestedCentre(
                this.product, viewCode, slug, index, total
            );
            return {
                id: `${viewCode}-${slug}`,
                slug,
                name: (ZONE_CATALOG[slug] || {}).label || slug,
                cx: centre.cx,
                cy: centre.cy,
            };
        },
        /**
         * Typing a length sets the scale AND, on a view with no zones,
         * STAMPS the standard set (ADR 0071 decision 2): one action yields
         * finished geometry to adjust rather than an empty canvas.
         */
        setInches({ viewCode, inches }) {
            const view = this.viewState(viewCode);
            const patch = { inches: inches > 0 ? inches : "", ppiSrc: "own" };
            let stamped = 0;
            if (inches > 0 && !view.zones.length) {
                const slugs =
                    (STANDARD_ZONES[this.product.cls] || {})[viewCode] || [];
                const drawable = slugs.filter((slug) =>
                    this.resolveZoneSize(viewCode, slug)
                );
                patch.zones = drawable.map((slug, i) =>
                    this.makeZone(viewCode, slug, i, drawable.length)
                );
                stamped = patch.zones.length;
            }
            /*
              Stamping the standard set with no photo on screen is the
              moment geometry becomes provisional -- the boxes were placed
              against the generic silhouette, not this blank.

              Only ever set here, never cleared: a later measurement taken
              WITH a photo does not re-check boxes that were stamped
              without one. Clearing is `confirmGeometry`'s job alone.
            */
            if (stamped && !this.hasImage(this.viewedColorId, viewCode)) {
                patch.provisional = true;
            }
            this.setViewState(viewCode, patch);
            if (stamped) {
                this.showToast(
                    `Scale set on ${viewCode} — stamped ${stamped} standard ` +
                        "zone" + (stamped > 1 ? "s" : "") + " at that scale. " +
                        "Nudge them into place."
                );
            } else if (inches > 0 && !this.offeredZonesFor(viewCode).length) {
                // Honest about the ADR's own gap rather than silently
                // stamping nothing.
                this.showToast(
                    "Scale set — no standard zones for this garment shape " +
                        "have a seeded printable area yet, so nothing was " +
                        "stamped."
                );
            }
        },
        setLine({ viewCode, line }) {
            // Moving a line means this view owns its own number from now on.
            this.setViewState(viewCode, { line, ppiSrc: "own" });
        },
        /**
         * Show or hide the scale line.
         *
         * NOTHING ELSE HAPPENS HERE, and that is the whole point. The
         * measured inches, `ppiSrc`, and every zone stay exactly as they
         * were, so hiding the ruler cannot cost anyone their geometry.
         * Jake asked for hide/show because the line "gets in the way" once
         * the scale is set -- a control that also reset the scale would
         * make the stage unusable for the reason it was added.
         */
        toggleLineVisibility() {
            this.lineVisible = !this.lineVisible;
        },
        addZone({ viewCode, slug }) {
            const zones = this.zonesFor(viewCode);
            if (zones.some((z) => z.slug === slug)) return;
            const next = [
                ...zones,
                this.makeZone(viewCode, slug, zones.length, zones.length + 1),
            ];
            this.writeZones(viewCode, next);
            // A box stamped by hand with no photo is as blind as one
            // stamped by the scale field, and carries the same warning.
            if (!this.hasImage(this.viewedColorId, viewCode)) {
                this.setViewState(viewCode, { provisional: true });
            }
        },
        /**
         * Write a view's boxes to whichever store the current color owns.
         *
         * An INHERITED color never reaches here -- the stage is read-only
         * for it (`can-edit` is false), because an edit there would fork it
         * silently and forking must be an explicit act (decision 4).
         */
        writeZones(viewCode, zones) {
            const colorId = this.viewedColor.id;
            const color = this.state.colors[colorId];
            if (color && color.mode === "override") {
                this.setState({
                    colors: {
                        ...this.state.colors,
                        [colorId]: {
                            ...color,
                            zones: { ...(color.zones || {}), [viewCode]: zones },
                        },
                    },
                });
                return;
            }
            this.setViewState(viewCode, { zones });
        },
        /**
         * Move a zone's centre.
         *
         * `dx`/`dy` are STEPS, not pixels: the step is an eighth of an inch
         * (an inch with `coarse`), converted to pixels through the view's
         * own scale. A pixel-based step would move a different REAL
         * distance on every SKU depending on its photo resolution, which is
         * the class of silent inconsistency this whole feature exists to
         * avoid.
         *
         * Writes through `writeZones`, so a nudge on the master reaches
         * every inherited color and a nudge on an inherited color is
         * impossible (the stage is read-only there).
         */
        nudgeZone({ viewCode, id, dx, dy, coarse = false }) {
            if (this.viewedColorMode === "inherit") return;
            const ppi = this.pxPerInchFor(viewCode);
            if (!ppi) return;
            const step = (coarse ? NUDGE_COARSE_IN : NUDGE_IN) * ppi;
            const zones = this.zonesFor(viewCode).map((z) =>
                z.id === id
                    ? { ...z, cx: z.cx + dx * step, cy: z.cy + dy * step }
                    : z
            );
            this.writeZones(viewCode, zones);
        },
        /** Remove a zone from a view. */
        deleteZone({ viewCode, id }) {
            if (this.viewedColorMode === "inherit") return;
            this.writeZones(
                viewCode,
                this.zonesFor(viewCode).filter((z) => z.id !== id)
            );
            if (this.selectedZoneId === id) this.selectedZoneId = null;
        },

        /**
         * A corner drag: record this garment's printable area for the zone.
         *
         * JAKE'S RULE. This writes `zoneSizes[view:slug]` -- a key with NO
         * COLOR IN IT -- so the edit reaches every color that inherits,
         * including when it was made with a color on screen. The buggy
         * implementation this forbids would key the write on
         * `this.viewedColor.id` and fork one color out of an edit that
         * looked global.
         *
         * The catalog figure is untouched, per decision 1.
         */
        resizeZone({ viewCode, slug, wIn, hIn }) {
            this.setState({
                zoneSizes: {
                    ...this.state.zoneSizes,
                    [`${viewCode}:${slug}`]: { wIn, hIn },
                },
            });
        },
        /**
         * Revert a zone to its CATALOG size by DELETING the override.
         *
         * Jake's rule: revert must delete, never copy the catalog's current
         * values into the row. A copy leaves the zone forked while LOOKING
         * reverted, so a later catalog change silently fails to reach it.
         */
        revertZoneSize({ viewCode, slug }) {
            this.setState({
                zoneSizes: revertZoneDimensions(
                    this.state.zoneSizes, viewCode, slug
                ),
            });
            this.showToast(
                `${(ZONE_CATALOG[slug] || {}).label || slug} uses its catalog ` +
                    "size again on this garment."
            );
        },
        /**
         * The filmstrip caption -- the SAME three words as the Geometry
         * column, deliberately.
         *
         * Jake asked for "drawn here" -> "primary" (2026-08-27). "same as
         * White" became "inherited from White" in the same pass: one
         * vocabulary for one concept, so the column and the caption
         * cannot be read as describing two different things. Both match
         * the v1 prototype, which rendered `'inherited from ' +
         * res.masterName` (ge-review.jsx:130).
         */
        colorModeLabel(colorId) {
            const mode = this.colorMode(colorId);
            if (mode === "master") return "primary";
            if (mode === "override") return "overridden";
            return `inherited from ${this.masterColorName}`;
        },
        /**
         * FORK a color: it stops following the definition and gets its own
         * boxes, copied for ALL views.
         *
         * All views deliberately (decision 4): "A front-only fork silently
         * falls back to the shared definition on the back, which is the same
         * class of invisible wrongness this ADR exists to prevent."
         *
         * Scale is NOT forked -- only boxes. There is one px_per_inch per
         * (SKU, view) and a colorway does not change how big an inch is.
         */
        makeColorDifferent(colorId) {
            const color = this.state.colors[colorId];
            if (!color || color.mode !== "inherit") return;
            const zones = Object.fromEntries(
                this.views.map((v) => [
                    v.code,
                    this.viewState(v.code).zones.map((z) => ({ ...z })),
                ])
            );
            this.setState({
                colors: {
                    ...this.state.colors,
                    [colorId]: { ...color, mode: "override", zones },
                },
            });
            this.showToast(
                `${this.viewedColor.name} now has its own boxes. Later edits ` +
                    `to ${this.masterColorName} will not reach it.`
            );
        },
        /**
         * REVERT a forked color to inheriting.
         *
         * Jake's rule again, at the color grain: this DROPS the color's
         * own boxes (`zones: null`) rather than copying the definition's
         * current boxes into them. A copy would leave the color forked
         * while looking reverted -- it would stop following later master
         * edits, invisibly. Dropping them makes it read THROUGH again.
         */
        revertColorToInherited(colorId) {
            const color = this.state.colors[colorId];
            if (!color || color.mode !== "override") return;
            this.setState({
                colors: {
                    ...this.state.colors,
                    [colorId]: { ...color, mode: "inherit", zones: null },
                },
            });
            this.showToast(
                `${this.viewedColor.name} follows ${this.masterColorName} ` +
                    "again."
            );
        },

        /**
         * The rail's gap line. Reports; never decides (ADR 0071
         * decision 6). Copy is specified by the handoff.
         */
        gapsFor(product) {
            const s = this.store[product.sku];
            // No state means no SKU (the empty-catalog placeholder, or a
            // rail row mid-hydration). Nothing to report a gap ON --
            // reporting one would print gap copy for a product that does
            // not exist.
            if (!s) return [];
            const out = [];
            const productViews = viewsForShape(product.cls);
            // A view with no usable px_per_inch is the gap that matters:
            // every decoration on it would render at a guessed size.
            for (const view of productViews) {
                const v = (s.views || {})[view.code];
                const inches = v ? Number(v.inches) : 0;
                if (!(inches > 0)) out.push(`no scale on ${view.label}`);
            }
            const unimaged = product.colors.filter((c) =>
                productViews.some((v) => !this.hasImageIn(s, c.id, v.code))
            ).length;
            if (unimaged) {
                out.push(
                    `${unimaged} color${unimaged > 1 ? "s" : ""} unimaged`
                );
            }
            /*
              PROVISIONAL GEOMETRY is a gap in the rail's own vocabulary,
              alongside "no scale on Back" and "1 color unimaged" -- it is
              the same kind of fact: something a human still has to do.

              Counted per VIEW and only where boxes actually exist, so a
              blank SKU reads "no scale", not "provisional geometry" about
              zones nobody has drawn.

              Still REPORTING, never deciding (ADR 0071 decision 6):
              `toggleReady` remains unconditional, so a superadmin can
              mark a SKU ready with provisional geometry and be told what
              they are accepting.
            */
            const provisional = productViews.filter((view) => {
                const v = (s.views || {})[view.code];
                return Boolean(
                    v && v.provisional && (v.zones || []).length > 0
                );
            }).length;
            if (provisional) {
                out.push(
                    `provisional geometry on ${provisional} view` +
                        (provisional > 1 ? "s" : "")
                );
            }
            return out;
        },
        railGapLine(product) {
            if (this.store[product.sku].ready) return "Ready";
            if (!product.colors.length) return "new — needs colors and images";
            const g = this.gapsFor(product);
            return g.length ? g.join(" · ") : "complete, not marked ready";
        },

        /**
         * Readiness is a human act. This handler is deliberately
         * unconditional: no gap check, no disabled state, no confirm.
         * ADR 0071 decision 6 -- "never computed and never blocked".
         */
        toggleReady() {
            this.setState({ ready: !this.state.ready });
        },

        toggleSize(size) {
            const sizes = this.state.sizes.includes(size)
                ? this.state.sizes.filter((s) => s !== size)
                : [...this.state.sizes, size];
            this.setState({ sizes });
        },

        /**
         * Changing the garment shape discards this SKU's geometry and
         * says so (ADR 0071 decision 7). The silhouette decides which
         * zones exist and where they sit, so boxes cannot survive it.
         *
         * In this phase there are no zone boxes to clear yet, so what
         * this resets is the state that DEPENDS on the shape: the view
         * set, the size vocabulary, and any per-color geometry fork.
         * The toast is the handoff's copy verbatim -- it stays correct
         * when 3c adds the boxes.
         */
        setGarmentShape(shape) {
            if (shape === this.product.cls) return;
            const nextViews = viewsForShape(shape);
            const nextVocabulary = sizeVocabularyForShape(shape);
            this.setProduct({ cls: shape, img: imgForShape(shape) });

            // Sizes that no longer exist in the new vocabulary cannot
            // stay selected (OSFA vs the apparel run).
            const sizes = this.state.sizes.filter((s) =>
                nextVocabulary.includes(s)
            );
            // Per-color geometry forks are dropped: an override is a
            // copy of boxes that no longer exist.
            const colors = Object.fromEntries(
                Object.entries(this.state.colors).map(([id, c]) => [
                    id,
                    {
                        ...c,
                        mode: c.mode === "override" ? "inherit" : c.mode,
                        zones: null,
                    },
                ])
            );
            // Image flags are re-keyed onto the new view set so a photo
            // attached to a view the shape no longer has stops counting.
            const images = Object.fromEntries(
                Object.entries(this.state.images).map(([id, flags]) => [
                    id,
                    Object.fromEntries(
                        nextViews.map((v) => [v.code, Boolean(flags[v.code])])
                    ),
                ])
            );
            /*
              ADR 0071 DECISION 7: the silhouette decides which zones exist
              and where they sit, so boxes cannot survive a shape change.
              Everything geometric is discarded -- the per-view boxes, the
              scale line and its measured inches, the per-garment printable
              area overrides, and every per-color fork.

              The SCALE goes too, not just the boxes. It is measured against
              a specific garment's photo; carrying it onto a different
              silhouette would leave a plausible-looking number that was
              never measured on THAT garment, which is precisely the silent
              wrongness this feature exists to prevent.
            */
            const views = Object.fromEntries(
                nextViews.map((v, i) => [
                    v.code,
                    {
                        line: defaultLineFor({
                            ...this.product,
                            cls: shape,
                            img: imgForShape(shape),
                        }),
                        inches: "",
                        ppiSrc: i === 0 ? "own" : "inherit",
                        zones: [],
                        // Cleared with the boxes it described. A flag that
                        // outlived its geometry would warn about zones
                        // that no longer exist.
                        provisional: false,
                    },
                ])
            );
            this.selectedZoneId = null;
            this.setState({
                sizes,
                colors,
                images,
                views,
                zoneSizes: {},
            });
            this.showToast(
                "Garment shape changed — geometry on this SKU was cleared, " +
                    "because its zones no longer exist."
            );
        },

        // --- colors -------------------------------------------------

        colorMode(colorId) {
            return (this.state.colors[colorId] || {}).mode || "inherit";
        },
        canRemoveColor(colorId) {
            // The definition holder cannot be removed while other
            // colors follow it -- that would strand their geometry.
            return (
                this.product.colors.length > 1 &&
                this.colorMode(colorId) !== "master"
            );
        },
        /**
         * Does this (colour, view) have a STORED image?
         *
         * GH #3112. `state.images[colorId][viewCode]` used to be a BOOLEAN
         * flipped by a button; it is now the server's `{url}` object for a
         * row in `local_garment_images`. The truthiness test reads the same
         * either way, which is why every existing caller
         * (`isProvisional`, the gap list, the geometry guards) keeps
         * working unchanged -- what changed is that the value can no longer
         * be true without a file behind it.
         */
        hasImageIn(state, colorId, viewCode) {
            const images = state.images[colorId];
            return Boolean(images && images[viewCode] && images[viewCode].url);
        },
        hasImage(colorId, viewCode) {
            return this.hasImageIn(this.state, colorId, viewCode);
        },
        /** The stored image URL for a (colour, view), or null. */
        imageUrl(colorId, viewCode) {
            const images = this.state.images[colorId];
            return (images && images[viewCode] && images[viewCode].url) || null;
        },
        /**
         * The dimensions to print under a filmstrip cell.
         *
         * The photo's REAL size once the file has reported one, and the shape-derived
         * frame otherwise.
         *
         * THE BUG THIS FIXES (Jake, 2026-08-31). This caption printed `product.img`
         * unconditionally -- the hardcoded 1600x1900 frame -- while the stage header a
         * few pixels away printed the photo's real `naturalWidth`/`naturalHeight`
         * (PR #3136). The same image was therefore labelled "1600x1900" in one place and
         * "1200x1800" in the other. On a page whose entire purpose is capturing a
         * measurement, a number that visibly contradicts the number beside it costs
         * trust in every other figure on the page.
         *
         * An unimaged color keeps the declared frame, which is the honest answer there:
         * with no file there is no real dimension to report, and the frame IS the space
         * that color's provisional boxes are authored in.
         */
        filmDims(colorId) {
            return this.filmNatural[colorId] || this.product.img;
        },

        /**
         * Record a filmstrip photo's decoded size.
         *
         * ON THE CELL WITH `.capture`, because `load` does not bubble -- the same
         * mechanism `GeometryStage.onPhotoLoad` uses and for the same reason. It must be
         * the PHOTO and not the silhouette's SVG art: that art's intrinsic size describes
         * a drawing, not this garment, and printing it would restore the wrong-number bug
         * with a different wrong number.
         *
         * A non-positive size is ignored rather than stored -- a broken or still-decoding
         * image reports 0x0, and "0x0" under a thumbnail is worse than the declared frame.
         */
        onFilmPhotoLoad(colorId, event) {
            const img = event.target;
            if (!img || !img.classList?.contains("garment-silhouette__photo")) return;
            const w = Number(img.naturalWidth);
            const h = Number(img.naturalHeight);
            if (!(w > 0) || !(h > 0)) return;
            this.filmNatural = { ...this.filmNatural, [colorId]: { w, h } };
        },

        /** True while an upload for this cell is in flight. */
        isUploading(colorId, viewCode) {
            return this.imageBusy[`${colorId}:${viewCode}`] === true;
        },
        /** The in-cell failure message for this (colour, view), or "". */
        imageError(colorId, viewCode) {
            return this.imageErrors[`${colorId}:${viewCode}`] || "";
        },
        /**
         * The picker's label.
         *
         * "Replace" rather than "Attach" once an image exists, because the
         * input stays available: swapping a photo is one pick, not a
         * remove-then-attach.
         */
        uploadLabel(colorId, viewCode) {
            if (this.isUploading(colorId, viewCode)) return "Uploading...";
            return this.hasImage(colorId, viewCode) ? "Replace" : "Attach image";
        },

        /**
         * A deterministic id, NOT a random one. The id reaches the DOM
         * as `sku-setup-color-row-<id>`, and a random suffix would
         * make the freshly-added row unaddressable by any stable
         * selector -- untestable from a ui-runner scenario, which is
         * exactly the surface that needs to click it. `new`, `new-2`,
         * `new-3` are unique within the SKU and stay readable.
         */
        nextColorId() {
            const taken = new Set(this.product.colors.map((c) => c.id));
            if (!taken.has("new")) return "new";
            let n = 2;
            while (taken.has(`new-${n}`)) n += 1;
            return `new-${n}`;
        },
        /**
         * Add a colourway with an EMPTY name.
         *
         * Jake, 2026-08-30: a new colour's name should be empty "since we
         * don't know what the colors may be." The old "New color" default
         * was a guess presented as an answer, and one a hurried superadmin
         * can save verbatim -- leaving a colourway literally NAMED "New
         * color" that `local_garment_images` and
         * `placement_zone_box_color_overrides` then key their rows on.
         *
         * The empty name is a row IN PROGRESS, not an invalid one: it
         * renders and is editable, and `rowFromProduct` omits it from the
         * write until it has a name (the backend requires one, and the
         * debounced auto-save would otherwise 400 on every keystroke
         * anywhere on the SKU until the colour was named).
         */
        addColor() {
            const id = this.nextColorId();
            const isFirst = this.product.colors.length === 0;
            this.setProduct({
                colors: [
                    ...this.product.colors,
                    { id, name: "", code: "", hex: "#b8bec3" },
                ],
            });
            this.setState({
                // No photos yet -- a new colour has nothing uploaded.
                images: { ...this.state.images, [id]: {} },
                colors: {
                    ...this.state.colors,
                    [id]: { mode: isFirst ? "master" : "inherit" },
                },
                master: isFirst ? id : this.state.master,
            });
        },

        /**
         * TYPING is not COMMITTING. The draft holds exactly what was
         * typed; nothing is canonicalized until the field is committed.
         *
         * WHY THIS SPLIT EXISTS -- it is the whole space bug. This
         * handler used to canonicalize on EVERY keystroke and write the
         * result straight back to a controlled `:value`. Canonicalizing
         * trims trailing whitespace, so typing "Athletic" then SPACE
         * re-rendered the field as "Athletic" and the space vanished
         * under the cursor. "Athletic Heather" -- a real SanMar color,
         * and one of the settled `color_sku` display names -- was
         * literally untypeable, and so was every other two-word name.
         *
         * Canonicalization itself was never wrong; its TIMING was. It
         * now runs in `commitColorName` at the commit boundary, which
         * is where the backend runs its own (`normalize_display_whitespace`
         * via `LocalGarmentImage.validate_color_name`).
         */
        draftColorName(colorId, rawName) {
            this.colorDrafts = { ...this.colorDrafts, [colorId]: rawName };
        },

        /**
         * Commit the draft: canonicalize the display name and, when it
         * matches a known SanMar color, adopt that color's supplier code
         * and hex too.
         *
         * A name that matches nothing is kept VERBATIM with an empty
         * code. Free-form is a requirement, not a fallback -- Phil's
         * AdventHealth matrix contains `Polished`, `Atlas`,
         * `Charter Blue` and one color literally named `Pending`, none
         * of which are SanMar names.
         */
        commitColorName(colorId) {
            const draft = this.colorDrafts[colorId];
            if (draft === undefined) return;
            const resolved = resolveTypedColor(draft);
            const patch = { name: resolved.name, code: resolved.code };
            // Only adopt a suggestion's hex; never clear a hex the user
            // already picked from the palette.
            if (resolved.hex) patch.hex = resolved.hex;
            this.setColor(colorId, patch);
            this.clearColorDraft(colorId);
        },

        clearColorDraft(colorId) {
            const drafts = { ...this.colorDrafts };
            delete drafts[colorId];
            this.colorDrafts = drafts;
        },

        /**
         * The value the name field shows: the in-progress draft while
         * typing, otherwise the committed name.
         */
        colorNameFieldValue(color) {
            const draft = this.colorDrafts[color.id];
            return draft === undefined ? color.name : draft;
        },

        /** Type-down suggestions for the row currently being edited. */
        colorSuggestionsFor(colorId) {
            const draft = this.colorDrafts[colorId];
            if (draft === undefined) return [];
            return filterColorSuggestions(draft);
        },

        /**
         * Adopt a suggestion wholesale -- name, supplier code and hex.
         * Mousedown rather than click fires BEFORE the field's blur, so
         * the option is still mounted when the pointer lands on it.
         */
        pickColorSuggestion(colorId, suggestion) {
            this.setColor(colorId, {
                name: suggestion.name,
                code: suggestion.code,
                hex: suggestion.hex,
            });
            this.clearColorDraft(colorId);
        },

        /**
         * The supplier's own color identifier, edited independently of
         * the display name. They are DIFFERENT identifiers and must not
         * collapse: `color_converter.csv` maps FIVE distinct mainframe
         * codes (`AthlHthr`, `Athletic Hthr`, `Athl Heather`, ...) onto
         * the single display name "Athletic Heather". The code is what a
         * purchase order carries; the name is what a shopper sees and
         * what our keys resolve on.
         *
         * Trimmed only -- a code is an opaque supplier token, so no
         * display-name canonicalization is applied to it.
         */
        setColorCode(colorId, rawCode) {
            this.setColor(colorId, { code: String(rawCode || "").trim() });
        },
        setColor(colorId, patch) {
            this.setProduct({
                colors: this.product.colors.map((c) =>
                    c.id === colorId ? { ...c, ...patch } : c
                ),
            });
        },
        removeColor(colorId) {
            if (!this.canRemoveColor(colorId)) return;
            this.setProduct({
                colors: this.product.colors.filter((c) => c.id !== colorId),
            });
            const images = { ...this.state.images };
            const colors = { ...this.state.colors };
            delete images[colorId];
            delete colors[colorId];
            this.setState({ images, colors });
        },
        /**
         * Write one (colour, view) image cell in local state.
         *
         * `entry` is the server's `{url}` object, or null to clear. The
         * ONLY writer of `state.images`, so there is one place where local
         * state can disagree with the server -- and it is only ever called
         * after a server round-trip has succeeded.
         */
        setImageEntry(colorId, viewCode, entry) {
            const forColor = { ...(this.state.images[colorId] || {}) };
            if (entry) {
                forColor[viewCode] = entry;
            } else {
                delete forColor[viewCode];
            }
            this.setState({
                images: { ...this.state.images, [colorId]: forColor },
            });
        },
        _setImageBusy(key, busy) {
            this.imageBusy = { ...this.imageBusy, [key]: busy };
        },
        _setImageError(key, message) {
            const errors = { ...this.imageErrors };
            if (message) {
                errors[key] = message;
            } else {
                delete errors[key];
            }
            this.imageErrors = errors;
        },
        /**
         * Upload a blank-garment photo and associate it with (colour, view).
         *
         * GH #3112, and the whole point of the issue. This replaced
         * `setImage(colorId, viewCode, true)`, which flipped a boolean and
         * rendered an "attached" chip with nothing stored.
         *
         * TWO REQUESTS, IN ORDER, and the order is load-bearing:
         *
         *   1. POST the bytes to `/api/upload_image` -- the SAME hardened
         *      MinIO route every other upload surface uses (Jake's explicit
         *      choice). A second upload mechanism here would fork the
         *      security contract: extension allowlist, declared-MIME check,
         *      50 MB bounded read, SVG sanitize.
         *   2. PUT the returned KEY as an association. The key, never a
         *      URL: the render path FETCHES whatever URL is stored, so a
         *      client-supplied one would let this page aim the renderer at
         *      any host. The backend derives the URL from its own base.
         *
         * LOCAL STATE IS WRITTEN ONLY AFTER BOTH SUCCEED. An optimistic
         * update would reintroduce exactly the defect being fixed -- a cell
         * that says "attached" with no stored file -- and would be worse
         * than the original, because after a real failure the user has
         * every reason to believe the upload worked.
         *
         * NOT routed through the debounced auto-save. That saver REPLACES
         * the whole colour list on every settled edit, so an in-flight save
         * from before the pick would silently erase the image that landed
         * during it. An image is attached by an explicit action, so it gets
         * an explicit request.
         */
        async uploadImage(color, viewCode, event) {
            const input = event && event.target;
            const file = input && input.files && input.files[0];
            const key = `${color.id}:${viewCode}`;
            // Clear the input straight away so picking the SAME file again
            // after a failure still fires a change event.
            const resetInput = () => {
                if (input) input.value = "";
            };
            if (!file) {
                resetInput();
                return;
            }

            const problem = describeUploadProblem(file);
            if (problem) {
                this._setImageError(key, problem);
                resetInput();
                return;
            }

            this._setImageError(key, "");
            this._setImageBusy(key, true);
            try {
                const form = new FormData();
                form.append("file", file);
                const uploaded = await axios.post("/api/upload_image", form, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const imageKey = uploaded && uploaded.data && uploaded.data.uuid;
                if (!imageKey) {
                    throw new Error("upload returned no key");
                }

                const assoc = await axios.put(this.imagesUrl(), {
                    color_name: color.name,
                    view_code: viewCode,
                    image_key: imageKey,
                });
                const url =
                    assoc && assoc.data && assoc.data.image
                        ? assoc.data.image.url
                        : null;
                if (!url) {
                    throw new Error("association returned no url");
                }
                this.setImageEntry(color.id, viewCode, { url });
            } catch (err) {
                // Names the STEP that failed. "Upload failed" for a stored
                // blob nobody points at would send the user looking at
                // their file rather than at the association.
                this._setImageError(
                    key,
                    `Upload failed -- ${describeRequestError(err)}`
                );
            } finally {
                this._setImageBusy(key, false);
                resetInput();
            }
        },
        /**
         * Remove the stored photo for one (colour, view).
         *
         * Server first, local state after -- for the same reason upload
         * writes last. A cleared cell over a row that still exists would
         * keep rendering the garment everywhere else while the superadmin
         * believes it is gone.
         */
        async removeImage(color, viewCode) {
            const key = `${color.id}:${viewCode}`;
            this._setImageError(key, "");
            this._setImageBusy(key, true);
            try {
                await axios.delete(this.imagesUrl(), {
                    data: { color_name: color.name, view_code: viewCode },
                });
                this.setImageEntry(color.id, viewCode, null);
            } catch (err) {
                this._setImageError(
                    key,
                    `Could not remove -- ${describeRequestError(err)}`
                );
            } finally {
                this._setImageBusy(key, false);
            }
        },
        /** The image collection route for the SELECTED SKU. */
        imagesUrl() {
            return (
                `/api/suppliers/non-integrated/${this.supplierId}` +
                `/skus/${encodeURIComponent(this.selectedSku)}/images`
            );
        },
        /**
         * Move the geometry definition to this color. The previous
         * holder becomes a follower -- there is exactly one definition
         * per SKU (ADR 0071 decision 4).
         */
        setMaster(colorId) {
            const colors = { ...this.state.colors };
            if (this.state.master && colors[this.state.master]) {
                colors[this.state.master] = {
                    ...colors[this.state.master],
                    mode: "inherit",
                };
            }
            colors[colorId] = { mode: "master" };
            this.setState({ master: colorId, colors });
        },

        // --- products ------------------------------------------------

        addProduct() {
            const n =
                this.products.filter((p) => p.sku.startsWith("NEW-")).length + 1;
            const sku = `NEW-${n}`;
            const product = {
                sku,
                itemNumber: "",
                name: "",
                cls: "tee",
                img: { ...DEFAULT_IMG },
                colors: [],
            };
            this.products.push(product);
            this.store[sku] = seedStateFor(product);
            // A brand-new SKU offers no sizes until someone picks them.
            this.store[sku].sizes = [];
            this.flushPendingSave();
            this.selectedSku = sku;
            this.viewedColorId = "";
            /*
              NOT saved on creation, deliberately.

              A new SKU has an empty name and item number, and the server
              REQUIRES both -- so an immediate save would PUT a row it is
              bound to reject and paint "Not saved -- name is required"
              across a form the user has not begun filling in. That reads
              as a malfunction rather than as a prompt.

              So a new SKU is local until its first real edit, and the
              gaps line in the rail already says it is incomplete. The
              first keystroke in Details queues the save through
              `setProduct` like any other edit. The cost is that a SKU
              created and abandoned with NO edits is not persisted, which
              is the right trade: there is nothing in it to lose.
            */
        },
    },
};
</script>

<style scoped>
/*
  Tokens come from styles/_tokens.css -- the handoff's palette and the
  repo's token file are already the same hues (#119ae9, #133752,
  #ef0405, #e0e0e0, #f5f8fa), so no new colors are introduced.
*/
/*
  The page is a normal-flow block inside SuperAdminLayout's `.sa-main`,
  which is ALREADY the scroll container (overflow-y:auto) inside a
  fixed-height, overflow:hidden `.sa-layout`. Do NOT add a second
  scroller or a min-height:100% here: an earlier revision did both, the
  root grew past the clipped parent, and everything below the fold --
  the colors table and the geometry section -- became unreachable.
  Caught by browser verification on 8086; the DOM-present assertions
  passed straight through it, because the elements existed.

  The rail scrolls independently and sticks to the viewport so a long
  product list does not push the sections down.
*/
.sku-setup {
    display: flex;
    align-items: flex-start;
    gap: 0;
    background: var(--tf-surface-light);
    position: relative;
    /* Cancel .sa-main's 24px padding so the rail meets the chrome. */
    margin: -24px;
}

/* --- rail --- */
.sku-setup__rail {
    width: 254px;
    flex: 0 0 254px;
    background: var(--tf-surface);
    border-right: 1px solid var(--tf-border);
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    max-height: 100vh;
}
.sku-setup__rail-head {
    padding: 12px 12px 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid var(--tf-border);
}
.sku-setup__rail-head .sku-setup__btn {
    margin-left: auto;
}

/* The auto-save indicator sits with the header controls. Error state is
   the one that must read at a glance, so it takes the danger hue. */
.sku-setup__save-state {
    font-size: 13px;
    color: var(--tf-text-muted, #6b7a86);
    margin-left: auto;
    padding-right: 12px;
}
.sku-setup__save-state--error {
    color: var(--tf-error);
    font-weight: 600;
}
.sku-setup__rail-list {
    overflow-y: auto;
    flex: 1;
}
/* The wrapper owns the chrome the row used to own, because the row and
   its delete are now siblings and must highlight as one line. */
.sku-setup__rail-item {
    display: flex;
    align-items: flex-start;
    border-left: 3px solid transparent;
}
.sku-setup__rail-item:hover {
    background: var(--tf-surface-alt);
}
.sku-setup__rail-item--on {
    background: var(--tf-primary-soft);
    border-left-color: var(--tf-primary);
}
.sku-setup__rail-row {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    text-align: left;
    padding: 10px 12px;
    background: transparent;
    border: 0;
    cursor: pointer;
}
/* ALWAYS VISIBLE, quiet rather than hidden. The reveal-on-hover pattern
   was the first cut and is wrong for this bug: the complaint (GH #3085)
   is that delete is not DISCOVERABLE, and a control you must already
   suspect is there to find is no more discoverable than one in a header.
   It also leaves an invisible-but-clickable target for anyone without
   hover. So it sits at the rail's quiet grey and takes its red on
   hover/focus, where the colour is a hint and the confirm panel is the
   guard. */
.sku-setup__rail-del {
    flex: none;
    align-self: center;
    margin-right: 6px;
    padding: 4px;
    display: inline-flex;
    background: transparent;
    border: 0;
    border-radius: 4px;
    color: var(--tf-fg-quiet);
    cursor: pointer;
}
.sku-setup__rail-del:hover,
.sku-setup__rail-del:focus-visible {
    color: #c0392b;
    background: var(--tf-surface);
}
.sku-setup__rail-del .material-icons {
    font-size: 17px;
}
.sku-setup__rail-body {
    flex: 1;
    min-width: 0;
}
.sku-setup__rail-name {
    display: block;
    font-size: 13px;
    color: var(--tf-fg-1);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.sku-setup__rail-meta {
    display: block;
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 10.5px;
    color: var(--tf-fg-quiet);
}
.sku-setup__rail-gaps {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: var(--tf-fg-quiet);
}
.sku-setup__rail-ready {
    font-size: 16px;
    color: var(--tf-success-dark);
}

/* --- page --- */
.sku-setup__main {
    flex: 1;
    min-width: 0;
}
.sku-setup__col {
    max-width: 1240px;
    margin: 0 auto;
    padding: 20px 28px 40px;
}
.sku-setup__header {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    margin-bottom: 18px;
}
.sku-setup__header-left {
    min-width: 0;
}
.sku-setup__header-right {
    margin-left: auto;
    text-align: right;
    /* Without a floor the gaps sentence wrapped mid-phrase
       ("no scale on Front - no / scale on Back"). */
    min-width: 260px;
}
.sku-setup__eyebrow {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--tf-fg-quiet);
}
.sku-setup__title {
    margin: 2px 0 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--tf-fg-1);
}
.sku-setup__strip {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 8px;
    font-size: 12.5px;
    color: var(--tf-fg-3);
}
.sku-setup__strip-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.sku-setup__dot {
    width: 8px;
    height: 8px;
    border-radius: 8px;
    background: #cfd8dc;
    display: inline-block;
}
.sku-setup__dot--done {
    background: var(--tf-success-dark);
}
.sku-setup__gaps {
    font-size: 12.5px;
    color: var(--tf-fg-3);
}
.sku-setup__gaps-note {
    font-size: 11px;
    color: var(--tf-fg-quiet);
}

/* --- cards --- */
.sku-setup__card {
    background: var(--tf-surface);
    border-radius: 12px;
    box-shadow: var(--tf-elev-2);
    padding: 20px;
    margin-bottom: 20px;
}
/*
  The card head WRAPS, and its hint is allowed to shrink.

  Both halves are needed and both were found by looking at a screenshot on
  8086 rather than by any DOM assertion. Adding the zoom stepper to the
  geometry head overflowed a nowrap row: the long hint ("Where decoration
  is allowed, and how big an inch is ...") held its intrinsic width, so
  the stepper was pushed past the card's right border and its 150% button
  was clipped in half. Every presence check passed regardless -- overflow
  is invisible to one, which is the same trap the stage caption hit when
  the provisional chip was added.

  `flex-wrap` lets the controls drop to their own line rather than off the
  card; `min-width: 0` on the hint lets it give up width first, so on a
  wide card they stay on one line. Controls keep `flex-shrink: 0` so the
  fix cannot instead squash the thing it was protecting.
*/
.sku-setup__card-head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
    min-width: 0;
}
.sku-setup__card-head .sku-setup__hint {
    min-width: 0;
}
.sku-setup__card-head .sku-setup__zoom,
.sku-setup__card-head .sku-setup__grid-toggle {
    flex-shrink: 0;
}
.sku-setup__secnum {
    width: 22px;
    height: 22px;
    border-radius: 22px;
    background: var(--tf-surface-dark);
    color: var(--tf-fg-3);
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 22px;
}
.sku-setup__card-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--tf-fg-1);
}
.sku-setup__hint {
    font-size: 12.5px;
    color: var(--tf-fg-3);
}

/* --- details --- */
.sku-setup__details {
    display: grid;
    /*
      The handoff specifies 170px 1fr 230px 200px, which assumes its
      standalone prototype's full width. Inside the SA chrome (220px
      sidebar + the 254px rail) those fixed tracks starved the 1fr
      "Product name" field to about one character wide -- verified in a
      browser on 8086. minmax() keeps the handoff's proportions while
      guaranteeing every field stays usable.
    */
    grid-template-columns:
        minmax(120px, 170px) minmax(200px, 1fr)
        minmax(170px, 230px) minmax(150px, 200px);
    gap: 20px;
    align-items: end;
}
.sku-setup__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}
.sku-setup__label {
    font-size: 11px;
    color: var(--tf-fg-quiet);
}
.sku-setup__input {
    border: 0;
    border-bottom: 1px solid var(--tf-border-strong);
    background: transparent;
    padding: 4px 0;
    font-size: 14px;
    color: var(--tf-fg-1);
    border-radius: 0;
}
.sku-setup__input:focus {
    outline: none;
    border-bottom-color: var(--tf-primary);
}
.sku-setup__input--mono {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
}
.sku-setup__input--name {
    width: 130px;
}
.sku-setup__input--code {
    width: 88px;
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 12px;
}

/*
  The type-down list. Its own positioning context on the name field
  wrapper rather than on `.sku-setup__color-cell`, which is already the
  anchor for the recolor palette popover -- sharing one context would
  stack the two overlays on the same origin.

  z-index sits ABOVE the palette (5) so a suggestion list opened while
  the palette happens to be hovered is still clickable.
*/
.sku-setup__name-wrap {
    position: relative;
    display: inline-flex;
}
.sku-setup__suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 6;
    margin-top: 4px;
    min-width: 190px;
    max-height: 240px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    padding: 4px;
}
.sku-setup__suggestion {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 7px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font: inherit;
}
.sku-setup__suggestion:hover,
.sku-setup__suggestion:focus-visible {
    background: rgba(0, 0, 0, 0.06);
}
.sku-setup__suggestion-chip {
    width: 12px;
    height: 12px;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    flex: 0 0 12px;
}
.sku-setup__suggestion-name {
    flex: 1 1 auto;
}
/* The code is secondary to the name but must stay VISIBLE -- seeing it
   beside the name is what teaches the two are different identifiers. */
.sku-setup__suggestion-code {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 11px;
    opacity: 0.6;
}
/* The free-form confirmation. Deliberately quiet -- it affirms, it does
   not warn: a color we do not stock a code for is a normal thing to
   type, not an error. */
.sku-setup__freeform {
    padding: 6px 7px;
    font-size: 12px;
    opacity: 0.7;
}

/* --- sizes --- */
.sku-setup__sizes {
    display: flex;
    align-items: center;
    gap: 22px;
    flex-wrap: wrap;
}
.sku-setup__size-tags {
    display: flex;
    gap: 5px;
}
.sku-setup__tag {
    font-size: 11.5px;
    padding: 3px 10px;
    border-radius: 50px;
    border: 1px solid var(--tf-border-strong);
    background: var(--tf-surface);
    color: var(--tf-fg-3);
    cursor: pointer;
}
.sku-setup__tag--on {
    background: var(--tf-primary-soft);
    border-color: var(--tf-primary);
    color: var(--tf-primary-press);
}
.sku-setup__sizeclass {
    display: flex;
    align-items: center;
    gap: 8px;
}
.sku-setup__seg {
    display: inline-flex;
    border: 1px solid var(--tf-border-strong);
    border-radius: 50px;
    overflow: hidden;
}
.sku-setup__seg button {
    border: 0;
    background: var(--tf-surface);
    padding: 4px 12px;
    font-size: 12px;
    color: var(--tf-fg-3);
    cursor: pointer;
}
.sku-setup__seg .sku-setup__seg--on {
    background: var(--tf-primary-soft);
    color: var(--tf-primary-press);
}

/* --- colors table --- */
.sku-setup__tbl {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    table-layout: auto;
}
/* Filenames are the widest cell content; let them wrap rather than
   push the last column off the card. */
.sku-setup__img-cell {
    white-space: normal;
}
.sku-setup__tbl th {
    background: #fafbfc;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: var(--tf-fg-3);
    text-align: left;
    padding: 14px;
    border-bottom: 1px solid var(--tf-border);
}
.sku-setup__tbl td {
    padding: 14px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
}
.sku-setup__tbl th:first-child,
.sku-setup__tbl td:first-child {
    padding-left: 0;
}
.sku-setup__tbl tbody tr:hover {
    background: #f5f8fa;
}
.sku-setup__cell-right {
    text-align: right;
}
.sku-setup__color-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
/* The inherited geometry cell carries BOTH its provenance chip and the
   action that claims the definition, so they need to sit together and
   wrap on a narrow column rather than stretching the row. */
.sku-setup__geo-cell {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}
.sku-setup__swatch {
    width: 14px;
    height: 14px;
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    display: inline-block;
    flex: 0 0 14px;
}
/*
  The recolor palette is a HOVER POPOVER, per the handoff ("14px swatch
  circle -- hover reveals an 8-swatch palette popover"). An earlier
  revision rendered all eight chips inline in every row: it crowded the
  color cell and pushed the table wide enough to clip the Back image
  column off the right edge. Verified in a browser on 8086.

  Kept in the DOM (not v-if) so it stays clickable by testid and the
  hover is a pure CSS concern -- opening it needs no state.
*/
.sku-setup__color-cell {
    position: relative;
}
.sku-setup__palette {
    display: flex;
    gap: 3px;
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 5;
    margin-top: 4px;
    padding: 6px;
    background: var(--tf-surface);
    border: 1px solid var(--tf-border);
    border-radius: 6px;
    box-shadow: var(--tf-elev-4);
    opacity: 0;
    visibility: hidden;
    transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}
.sku-setup__color-cell:hover .sku-setup__palette,
.sku-setup__palette:focus-within {
    opacity: 1;
    visibility: visible;
}
.sku-setup__palette-chip {
    width: 14px;
    height: 14px;
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    padding: 0;
    cursor: pointer;
    flex: 0 0 14px;
}
.sku-setup__img-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
/* GH #3112 -- the real upload cell. */
.sku-setup__img-has {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
/*
  The stored blank, small. `object-fit: contain` rather than `cover`
  because a garment photo cropped to a square stops showing the garment --
  and the whole reason this thumbnail exists is so a superadmin can see
  WHICH photo is attached without opening it.
*/
.sku-setup__thumb {
    width: 40px;
    height: 40px;
    object-fit: contain;
    border: 1px solid var(--tf-border);
    border-radius: 4px;
    background: #fff;
}
/*
  A file input cannot be styled to match the page's buttons, so the LABEL
  carries the button classes and the input is visually hidden inside it.
  Clicking the label opens the picker natively -- no JS proxy click, which
  keeps keyboard and assistive-tech activation working for free.

  NOT `display: none` or `visibility: hidden`: both remove the input from
  the accessibility tree and, in some browsers, from keyboard focus order
  entirely. The clip-rect pattern keeps it focusable and announced while
  taking no visual space.
*/
.sku-setup__file {
    position: relative;
    overflow: hidden;
    cursor: pointer;
}
.sku-setup__file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
/*
  A failed upload, stated in the cell it failed in. Uses the page's error
  token so it reads as a failure at a glance rather than as a hint.
*/
.sku-setup__img-error {
    display: block;
    flex-basis: 100%;
    color: var(--tf-error);
    font-size: 12px;
    line-height: 1.35;
}
/* The two main-body placeholders. Centred and generously padded so the
   column does not read as a page that failed to load -- this is a state,
   not an error, and it carries the next action. */
.sku-setup__blank {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 320px;
    padding: 48px 24px;
    text-align: center;
}
.sku-setup__blank-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--tf-fg-1);
}
.sku-setup__blank-body {
    margin: 0;
    max-width: 46ch;
    font-size: 14px;
    line-height: 1.5;
    color: var(--tf-fg-2);
}
/* The upload gate's reason. Muted, NOT error-coloured: nothing has gone
   wrong here, the SKU is simply not saved yet. Red would report a failure
   for a form the user is still filling in. */
.sku-setup__img-gate {
    display: block;
    flex-basis: 100%;
    color: var(--tf-fg-2);
    font-size: 12px;
    line-height: 1.35;
}
/* The label is the visible control -- the real input under it is clipped,
   so a `:disabled` input alone would leave the button looking live. */
.sku-setup__file--off {
    opacity: 0.55;
    cursor: not-allowed;
}
.sku-setup__mono {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 12px;
    color: var(--tf-fg-2);
}
.sku-setup__x {
    border: 0;
    background: transparent;
    font-size: 17px;
    line-height: 1;
    color: var(--tf-fg-quiet);
    cursor: pointer;
    padding: 0 2px;
}
.sku-setup__x:hover {
    color: var(--tf-red);
}

/* --- chips + buttons --- */
.sku-setup__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 50px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}
.sku-setup__chip-dot {
    width: 7px;
    height: 7px;
    border-radius: 7px;
    background: currentColor;
    display: inline-block;
}
.sku-setup__chip--ok {
    background: var(--tf-chip-success-bg);
    color: var(--tf-success-dark);
}
.sku-setup__chip--warn {
    background: var(--tf-warning-soft);
    color: var(--tf-warning-fg);
}
.sku-setup__btn {
    height: 40px;
    padding: 0 22px;
    border-radius: 50px;
    border: 1px solid var(--tf-border-strong);
    background: var(--tf-surface);
    color: var(--tf-fg-2);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
}
.sku-setup__btn:hover {
    background: var(--tf-surface-alt);
}
.sku-setup__btn--primary {
    background: var(--tf-primary);
    border-color: var(--tf-primary);
    color: var(--tf-fg-on-primary);
}
.sku-setup__btn--primary:hover {
    background: var(--tf-primary-hover);
}
.sku-setup__btn--xs {
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
}

/* --- geometry placeholder --- */

/* --- destructive actions --- */
/* Colour is a hint that an action destroys, never the guard. The guard
   is the confirm panel, which states the consequence in words. */
.sku-setup__rail-actions {
    display: inline-flex;
    gap: 6px;
}
.sku-setup__btn--danger {
    border-color: #c0392b;
    color: #c0392b;
}
.sku-setup__btn--danger:disabled {
    opacity: 0.6;
    cursor: default;
}
.sku-setup__danger {
    margin: 16px 0;
    padding: 16px;
    border: 2px solid #c0392b;
    border-radius: 8px;
    background: #fdf3f2;
}
.sku-setup__danger-title {
    margin: 0 0 8px;
    color: #c0392b;
}
.sku-setup__danger-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}

/* --- toast --- */
.sku-setup__toast {
    position: fixed;
    left: 50%;
    bottom: 28px;
    transform: translateX(-50%);
    background: var(--tf-secondary);
    color: var(--tf-fg-on-secondary);
    padding: 10px 18px;
    border-radius: 6px;
    box-shadow: var(--tf-elev-8);
    font-size: 13px;
    z-index: 20;
    max-width: 640px;
}

/* --- 4 geometry (phase 3c) --- */
.sku-setup__spacer {
    flex: 1 1 auto;
}
/* The zoom stepper. Segmented, like the v1 control it restores. */
.sku-setup__zoom {
    display: inline-flex;
    border: 1px solid var(--tf-border);
    border-radius: 50px;
    overflow: hidden;
}
.sku-setup__zoom-btn {
    padding: 4px 10px;
    border: none;
    background: var(--tf-surface);
    font-size: 11.5px;
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    color: var(--tf-fg-3);
    cursor: pointer;
}
.sku-setup__zoom-btn + .sku-setup__zoom-btn {
    border-left: 1px solid var(--tf-border);
}
.sku-setup__zoom-btn:hover {
    background: var(--tf-surface-alt);
}
.sku-setup__zoom-btn--on {
    background: var(--tf-primary, #119ae9);
    color: #fff;
}
.sku-setup__grid-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: var(--tf-fg-3);
    cursor: pointer;
    white-space: nowrap;
}
/*
  The banner naming which color is on the stage. Blue while a color
  inherits or holds the definition, amber once it is forked -- the ink
  vocabulary from ADR 0071 decision 5 ("inheritance is shown, not
  asserted"), matching the zone strokes on the stage itself.
*/
.sku-setup__geo-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 6px;
    background: var(--tf-primary-soft, #e3f2fd);
    color: #0a78b8;
    font-size: 12.5px;
    margin-bottom: 14px;
}
.sku-setup__geo-banner--forked {
    background: #fff8e1;
    color: #b26a00;
}
.sku-setup__mini-btn {
    height: 32px;
    padding: 0 14px;
    border-radius: 50px;
    border: 1px solid var(--tf-border);
    background: var(--tf-surface);
    font-size: 13px;
    font-weight: 500;
    color: var(--tf-fg-2);
    cursor: pointer;
    white-space: nowrap;
    transition: background 150ms cubic-bezier(0.4, 0, 0.2, 1),
        border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}
.sku-setup__mini-btn:hover {
    background: var(--tf-surface-alt);
}
/*
  Two stages SIDE BY SIDE, as the handoff specifies -- front and back
  together are the comparison the reviewer is making.

  `minmax(0, 1fr)` rather than `1fr`: a plain 1fr floors at the track's
  min-content width, and a stage's min-content includes its nowrap caption
  row, so the grid would push past the card border rather than letting the
  stages shrink. This is why the handoff pinned a fixed 558px in its own
  prototype; inside the SA chrome the available width is not fixed, so the
  stage is fluid and the 558px becomes a MAXIMUM instead.

  Auto-fit was tried first and was wrong: at the real SA viewport (a 220px
  sidebar plus the 254px rail) the track floor could only fit ONE column,
  so the two stages stacked and the comparison was lost. Verified in the
  browser on 8086 -- the DOM-present assertions passed either way.
*/
.sku-setup__geometry-error {
    margin: 0 0 12px;
    color: var(--tf-error);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
}
.sku-setup__stages {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 28px;
}
.sku-setup__filmstrip-wrap {
    border-top: 1px solid var(--tf-border);
    margin-top: 20px;
    padding-top: 14px;
}
.sku-setup__filmstrip {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 10px;
}
.sku-setup__film-cell {
    width: 136px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    border: 1px solid var(--tf-border);
    border-radius: 6px;
    background: var(--tf-surface);
    cursor: pointer;
    text-align: left;
}
.sku-setup__film-cell--on {
    outline: 2px solid var(--tf-primary, #119ae9);
    outline-offset: 1px;
}
.sku-setup__film-art {
    width: 100%;
    height: 96px;
}
.sku-setup__film-name {
    font-size: 12px;
    color: var(--tf-fg-2);
}
.sku-setup__film-cap {
    font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
    font-size: 10.5px;
    color: var(--tf-fg-3);
}
</style>
